import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import ActivityList from './components/ActivityList';
import Chat from './pages/Chat';
import Profile from './pages/Profile';
import WeeklyPlan from './pages/WeeklyPlan';
import Races from './pages/Races';
import OnboardingWizard from './pages/OnboardingWizard';
import OnboardingLoader from './pages/OnboardingLoader';
import { CYCLING_ENABLED, GARMIN_ENABLED, PREMIUM_TODAY_ENABLED, HOME_GRAPHITE_ENABLED } from './config/features';
import HomeGraphiteHome from './components/today/HomeGraphiteHome';
import { GraphiteBottomNav } from './components/today/HomeGraphite';
import CoachDashboard from './pages/CoachDashboard';
import JoinCoach from './pages/JoinCoach';
import AthleteMessages from './pages/AthleteMessages';
import AdminPanel from './pages/AdminPanel';
import InstallPrompt from './components/InstallPrompt';
import NotificationButton from './components/NotificationButton';
import AuthPage from './pages/AuthPage';
import ConsentScreen from './components/ConsentScreen';
import { CONSENT_VERSION } from './config/consent';
import PrivacyPolicy from './components/PrivacyPolicy';
import PrivacyPolicyEN from './components/PrivacyPolicyEN';
import TermsOfService from './components/TermsOfService';
import Footer from './components/Footer';
import Home from './pages/Home';
import { RaceHero, TodayWorkout, InsightA2, ChatCardA2, PrivilegedSection, A2Banner, QuickAccessList } from './components/home2/HomeA2';
import TrainingSourcesCard from './components/home2/TrainingSourcesCard';
import DeepAnalysisViewer from './components/home2/DeepAnalysisViewer';
import PremiumToday from './components/today/PremiumToday';
import PremiumNavShell from './components/today/PremiumNavShell';
import { fetchSavedDeepAnalysis } from './utils/deepAnalysis';
import { Header, Button, Skeleton } from './components/ui';
import { User, FirstAid, Warning, UserPlus, ChatCircle } from '@phosphor-icons/react';
import { onAuthChange, logOut, refreshPushTokenIfNeeded } from './firebase';
import authFetch from './utils/authFetch';
import { showToast } from './utils/toast';
import { runPlanRebuild } from './utils/planRebuild';
import { confirmNavigation } from './utils/navigationGuard';
import { useBackEntry } from './hooks/useBackEntry';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://claude-coach-backend-1.onrender.com';

// ─── Global toast host ────────────────────────────────────────────────
// Renders a single toast at a time, anchored bottom-center. Triggered
// from anywhere via `showToast(message, type)` (utils/toast.js), which
// dispatches an 'app-toast' window event. Replaces native alert() calls
// across the app — same UX as the plan-regen banner: passive, dismisses
// itself, doesn't steal focus.
function ToastHost() {
  const [toast, setToast] = useState(null);
  useEffect(() => {
    let dismissTimer = null;
    const onToast = (e) => {
      const { message, type = 'success', duration = 3500 } = e.detail || {};
      if (!message) return;
      setToast({ message, type });
      clearTimeout(dismissTimer);
      dismissTimer = setTimeout(() => setToast(null), duration);
    };
    window.addEventListener('app-toast', onToast);
    return () => {
      window.removeEventListener('app-toast', onToast);
      clearTimeout(dismissTimer);
    };
  }, []);
  if (!toast) return null;
  const tone =
    toast.type === 'error'
      ? 'bg-rose-500/20 border-rose-500/30 text-rose-300'
      : toast.type === 'info'
      ? 'bg-sky-500/20 border-sky-500/30 text-sky-300'
      : 'bg-emerald-500/20 border-emerald-500/30 text-emerald-300';
  const icon = toast.type === 'error' ? '⚠️' : toast.type === 'info' ? '💡' : '✅';
  return (
    <div
      role="status"
      aria-live="polite"
      dir="rtl"
      className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[80]
        px-4 py-3 rounded-xl text-sm font-medium shadow-lg
        flex items-center gap-2 transition-all border ${tone}`}
    >
      <span>{icon}</span>
      <span>{toast.message}</span>
    </div>
  );
}

// Build marker — logged at module load so a quick console check tells
// us whether the current tab is on the State 1 bundle (with the
// fullscreen OnboardingLoader) or an older cached one. Bump the date
// when you ship a new architectural shift.
console.log('🏗️ App bundle: BGSYNC_LOOP_FIX (2026-06-07)');

function App() {
  const [isOffline, setIsOffline] = useState(false);
  // A2 redesign: the waking-banner was removed (§1.9) — only the setter remains in use
  // by the health poller; the elided first slot avoids an unused-var warning.
  const [, setServerWaking] = useState(false); // Render cold start
  const [checkingConnection, setCheckingConnection] = useState(true);
  const connectionReadyRef = useRef(false); // ref עבור closure-safe access
  const authCascadeUidRef = useRef(null); // run the heavy user-type/onboarding check cascade once per uid
  const insightsLoadingRef = useRef(false); // prevent concurrent loadHomeInsights calls
  const wtFetchIdRef = useRef(0); // stale-fetch guard: only the latest loadWorkoutTrend saves
  // Garmin pilot: Stridee returns the browser to /garmin-return → open Profile, where the Garmin card reads it.
  // Where the race list's back button returns to: the chat when the coach's "מעבר ללוח התחרויות" opened it,
  // home otherwise. Navigation only — the chat reloads its own history when it mounts again.
  const [racesReturnTo, setRacesReturnTo] = useState(null);
  // 2026-10-29: the chat's "מעבר לפרופיל" — open at a card, "חזרה" back to the chat
  const [profileReturnTo, setProfileReturnTo] = useState(null);
  const [profileFocus, setProfileFocus] = useState(null);
  const [currentPage, setCurrentPage] = useState(() => (
    GARMIN_ENABLED && window.location.pathname === '/garmin-return' ? 'profile' : 'home'));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileComplete, setProfileComplete] = useState(null);
  const [profile, setProfile] = useState(null);
  const [nextRace, setNextRace] = useState(null);
  const [nextGoalRace, setNextGoalRace] = useState(null);
  const [hasConsented, setHasConsented] = useState(false);
  const [checkingConsent, setCheckingConsent] = useState(false);
  const [connectionError, setConnectionError] = useState(false); // 🔧 FIX: Track API failures
  const [consentVersionMismatch, setConsentVersionMismatch] = useState(false); // server consent version ≠ this build
  
  // Onboarding state
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  
  // Home data readiness (Strava removal C2, 2026-10-21): replaces the old Strava status as the Home skeleton gate.
  const [homeDataReady, setHomeDataReady] = useState(false);
  // Strava activities are gone (C1/C2); kept as the always-empty input of the coach insight / chat chips.
  const [lastActivity] = useState(null);
  // localStorage cache helpers — stale-while-revalidate, 24h TTL
  const _lsGet = (key) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const { data, ts } = JSON.parse(raw);
      if (Date.now() - ts > 86400000) { localStorage.removeItem(key); return null; }
      return data;
    } catch { return null; }
  };
  const _lsSet = (key, data) => {
    try { localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() })); } catch {}
  };

  const [homeInsights, setHomeInsights] = useState(null);
  const [homeDismissed, setHomeDismissed] = useState([]);
  const [workoutTrend, setWorkoutTrend] = useState(null);
  const saveHomeInsights = (data) => { _lsSet('hi_cache', data); setHomeInsights(data); };
  const saveWorkoutTrend = (data) => { _lsSet('wt_cache', data); setWorkoutTrend(data); };
  const [pendingContextWorkout, setPendingContextWorkout] = useState(null);
  const [contextSaving, setContextSaving] = useState(false);
  const [insightsRefreshing, setInsightsRefreshing] = useState(false);
  const [deepAnalyzing, setDeepAnalyzing] = useState(false);
  // "צפה בניתוח" (2026-10-23): the SAVED deep analysis, read only (GET /chat/deep-analysis) — never a generation
  const [deepView, setDeepView] = useState(null);   // null | { loading, text, date }
  const [insightsExpanded, setInsightsExpanded] = useState(false);
  const [plannedWeeklyKm, setPlannedWeeklyKm] = useState(null);
  const [weeklyPlanPhase, setWeeklyPlanPhase] = useState(null); // שלב אימון מהתכנית
  
  // User type state (b2c = independent, b2b = has coach, coach = is coach)
  const [userType, setUserType] = useState('b2c');
  // B2B chat upgrade modal — paywall for athletes attached to a coach.
  // Free flow: coach is the AI. Upgrade (₪19.90/mo) → direct AI chat too.
  const [showChatUpgradeModal, setShowChatUpgradeModal] = useState(false);
  const [checkingUserType, setCheckingUserType] = useState(true);
  const [myCoach, setMyCoach] = useState(null);
  
  // Invite code from URL (for joining a coach)
  const [inviteCode, setInviteCode] = useState(null);
  const [pendingInviteCode, setPendingInviteCode] = useState(null);

  // 🆕 Message usage state for home screen
  const [dailyMessageCount, setDailyMessageCount] = useState(0);
  const [dailyLimit, setDailyLimit] = useState(10);
  const [messagesRemaining, setMessagesRemaining] = useState(10);
  const [chatCard, setChatCard] = useState(null);
  const [chatCardLoading, setChatCardLoading] = useState(false);

  // בדיקת תקשורת מיידית בטעינה — לפני שהמשתמש נכנס לאפליקציה
  const [connectingMsg, setConnectingMsg] = useState('בודק חיבור...');

  useEffect(() => {
    const checkOnStart = async () => {
      // אם navigator.onLine=false — אין אינטרנט בכלל
      if (!navigator.onLine) {
        setIsOffline(true);
        connectionReadyRef.current = true;
        setCheckingConnection(false);
        return;
      }

      // ניסיון ראשון מהיר (8 שניות)
      try {
        await authFetch(`${API_BASE_URL}/health`, { signal: AbortSignal.timeout(8000) });
        setIsOffline(false);
        connectionReadyRef.current = true;
        setCheckingConnection(false);
        return;
      } catch {}

      // Render מתעורר — הצג "מאתחל שרת" ונסה עוד עד 45 שניות
      setServerWaking(true);
      setCheckingConnection(false); // תן לאפליקציה להמשיך לטעון
      connectionReadyRef.current = true;

      for (let attempt = 1; attempt <= 6; attempt++) {
        await new Promise(r => setTimeout(r, 7000));
        try {
          await authFetch(`${API_BASE_URL}/health`, { signal: AbortSignal.timeout(10000) });
          setServerWaking(false);
          setIsOffline(false);
          return;
        } catch {}
      }

      // אחרי 45 שניות — כנראה באמת אין חיבור
      setServerWaking(false);
      setIsOffline(!navigator.onLine);
    };
    checkOnStart();
  }, []);

  // Offline detection — navigator.onLine + active API ping
  useEffect(() => {
    let offlineTimer = null;
    let failCount = 0;

    // הבחנה: navigator.onLine=false → אין אינטרנט אמיתי → מסך חסימה
    //         navigator.onLine=true + ping נכשל → שרת עסוק (תכנית) → banner בלבד
    //
    // 2026-06-10: lowered threshold from 6 → 2 consecutive failures
    // (= 60 sec instead of 180 sec) after user reported UX during Render
    // deploys was unacceptable — empty screen for 3 minutes while
    // backend restarts. With threshold=2, the banner appears after 1 min,
    // giving the user a clear "המערכת מתעדכנת" signal instead of a
    // confusing blank state.
    const pingOnce = () =>
      fetch(`${API_BASE_URL}/health`, { signal: AbortSignal.timeout(12000) })
        .then(() => { failCount = 0; setIsOffline(false); setServerWaking(false); })
        .catch(() => {
          failCount++;
          if (!navigator.onLine) {
            // אין אינטרנט → מסך חסימה
            setIsOffline(true);
          } else if (failCount >= 2) {
            // navigator.onLine=true אבל שרת לא עונה 60s+ → banner "השרת מתעדכן"
            setServerWaking(true);
            setIsOffline(false);
          }
        });

    const confirmOffline = () => {
      if (!navigator.onLine) { setIsOffline(true); return; }
      // navigator.onLine=true — כנראה שרת עסוק, לא אין אינטרנט
      setServerWaking(true);
    };

    const goOffline = () => {
      clearTimeout(offlineTimer);
      offlineTimer = setTimeout(confirmOffline, 5000); // 5s delay for mobile handoff
    };
    const goOnline = () => {
      clearTimeout(offlineTimer);
      failCount = 0;
      fetch(`${API_BASE_URL}/health`, { signal: AbortSignal.timeout(10000) })
        .then(() => setIsOffline(false))
        .catch(() => {});
    };
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);

    // Ping every 30s (was 20s) — 3 consecutive failures × 30s = 90s before marking offline
    // Plan generation takes ~30-50s, so at most 2 pings fire — won't trigger offline
    const pingInterval = setInterval(() => pingOnce(), 30000);

    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
      clearInterval(pingInterval);
      clearTimeout(offlineTimer);
    };
  }, []);

  // Check URL for invite code on mount
  useEffect(() => {
    const path = window.location.pathname;
    const match = path.match(/^\/join\/([^\/]+)$/);
    if (match) {
      console.log('📨 Invite code detected:', match[1]);
      setInviteCode(match[1]);
      localStorage.setItem('pending_invite_code', match[1]);
    } else {
      const savedInvite = localStorage.getItem('pending_invite_code');
      if (savedInvite) {
        console.log('📨 Found pending invite code:', savedInvite);
        setPendingInviteCode(savedInvite);
      }
    }
  }, []);

  // F5-resume helper (2026-06-04). Asks the backend whether the user
  // has an in-flight plan generation, and if so re-attaches the
  // global progress banner + a quiet 7-second poll loop so the
  // refreshed page surfaces the same UX it had before the F5.
  // Fully fire-and-forget — never blocks the caller.
  const resumeInFlightPlanGeneration = async (userId) => {
    let res;
    try {
      res = await authFetch(`${API_BASE_URL}/plan-status/current?user_id=${encodeURIComponent(userId)}`);
    } catch (_) { return; }
    if (!res || !res.ok) return;
    let data;
    try { data = await res.json(); } catch (_) { return; }
    if (!data || data.status !== 'processing' || !data.job_id) {
      // No in-flight job. But a banner may still be persisted as 'running'
      // in sessionStorage — e.g. onboarding kicked off a batch, then the
      // full-page Strava OAuth redirect reloaded the app. The original
      // planRegenRequest promise died with the old page, so nothing will
      // ever fire 'plan-regen-done' and the banner hangs forever
      // ("בונה תכנית … X דק' עברו" with no completion). The job itself
      // already finished server-side (status='completed'), so detect the
      // stale banner and close it now, colouring by the finished status.
      try {
        if (window.sessionStorage.getItem('planRegenStatus')) {
          const _ok = !(data && data.status === 'failed');
          console.log(`🔁 resume: stale running banner + job ${data?.status || 'none'} → closing`);
          window.dispatchEvent(new CustomEvent('plan-regen-done', { detail: { success: _ok } }));
        }
      } catch (_) {}
      return;
    }

    const sport = (data.request_meta && data.request_meta.sport_type) || '';
    const weekHe = (data.request_meta && data.request_meta.week_type) === 'next' ? 'שבוע הבא' : 'שבוע נוכחי';
    const sportHe = sport === 'cycling' ? 'רכיבה' : 'ריצה';
    const changedFields = sport ? [`תכנית ${sportHe} — ${weekHe}`] : [];
    console.log(`🔁 F5 resume: re-attaching banner for job ${data.job_id.slice(0,8)}`);
    // Set the global in-flight flag BEFORE dispatching the banner.
    // WeeklyPlan's bgSync useEffect runs on mount and reads this flag
    // to decide whether to skip its own per-task generate-v2 calls
    // (which would otherwise produce premature 'ready' toasts during
    // a global batch — user report 2026-06-07 after F5).
    try { window.__planRegenInFlight = true; } catch {}
    window.dispatchEvent(new CustomEvent('plan-regen-start', { detail: { changedFields } }));

    // Quiet polling every 7s — slower than active polling (3s) to
    // avoid hammering the server while it's busy generating. Hard
    // cap at MAX_POLL_MS so we don't leak a polling loop if the
    // backend stays stuck.
    const POLL_INTERVAL_MS = 7000;
    const MAX_POLL_MS = 8 * 60 * 1000;
    const start = Date.now();
    let finished = false;
    while (!finished && Date.now() - start < MAX_POLL_MS) {
      await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
      try {
        const pr = await authFetch(
          `${API_BASE_URL}/plan-status/${encodeURIComponent(data.job_id)}`
          + `?user_id=${encodeURIComponent(userId)}`
        );
        if (!pr.ok) continue;
        const pdata = await pr.json();
        if (pdata && pdata.status && pdata.status !== 'processing') {
          finished = true;
          const success = pdata.status === 'completed';
          window.dispatchEvent(new CustomEvent('plan-regen-done', { detail: { success } }));
        }
      } catch (_) { /* keep polling */ }
    }
    if (!finished) {
      console.log('🔁 F5 resume: poll timed out at 8min — letting the banner clear');
      window.dispatchEvent(new CustomEvent('plan-regen-done', { detail: { success: false } }));
    }
  };

  // 🆕 Load message usage from server
  const loadMessageUsage = async (userId) => {
    // Cache-first (2026-06-05): during a heavy plan generation, the
    // Render Standard CPU is saturated by Gemini Pro and this endpoint
    // returns 'Failed to fetch'. Without a cache the home tab shows
    // blank message-usage during the whole 3-5 minute batch. Apply
    // the cached value immediately, then refresh in the background.
    try {
      const _cached = localStorage.getItem(`message_usage_${userId}`);
      if (_cached) {
        const data = JSON.parse(_cached);
        setDailyMessageCount(data.message_count || 0);
        setDailyLimit(data.daily_limit || 10);
        setMessagesRemaining(data.remaining || 10);
      }
    } catch (_) { /* fall through to network */ }
    try {
      const response = await authFetch(`${API_BASE_URL}/message-usage/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setDailyMessageCount(data.message_count || 0);
        setDailyLimit(data.daily_limit || 10);
        setMessagesRemaining(data.remaining || 10);
        try { localStorage.setItem(`message_usage_${userId}`, JSON.stringify(data)); } catch (_) {}
        console.log(`📊 Home usage: ${data.message_count}/${data.daily_limit}`);
      }
    } catch (error) {
      // Stale cached value is already on screen — silent fallback.
      console.log('📦 message-usage: using cached value (backend busy)');
    }
  };

  // Consent is decided ONLY by the backend (GET /consent/check, consent version 2.0+). No
  // localStorage value can grant access: if the server can't be reached after the retries, the
  // user stays on the connection-error screen (fail-closed), never in the app and never on the
  // ConsentScreen as if they had no consent.
  const checkUserConsent = async (userId, retryCount = 0) => {
    setCheckingConsent(true);
    setConnectionError(false);
    setConsentVersionMismatch(false);
    try {
      // Legacy unversioned cache (consent_${uid}) — never read; removed.
      try { localStorage.removeItem(`consent_${userId}`); } catch { /* ignore */ }

      const response = await authFetch(`${API_BASE_URL}/consent/check?user_id=${userId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.current_version && data.current_version !== CONSENT_VERSION) {
          console.warn(`⚠️ Consent version mismatch: server ${data.current_version}, app ${CONSENT_VERSION}`);
          setHasConsented(false);
          setConsentVersionMismatch(true);
        } else {
          setHasConsented(data.has_consented === true);
        }
        return;
      }
      if (retryCount < 3) {
        console.log(`⚠️ Consent check failed (${response.status}), retrying... (${retryCount + 1}/3)`);
        await new Promise(r => setTimeout(r, 2500));
        return checkUserConsent(userId, retryCount + 1);
      }
      console.error('❌ All consent check retries failed');
      setHasConsented(false);
      setConnectionError(true);
    } catch (error) {
      console.error('Error checking consent:', error);
      if (retryCount < 3) {
        console.log(`⚠️ Network error, retrying consent check... (${retryCount + 1}/3)`);
        await new Promise(r => setTimeout(r, 2500));
        return checkUserConsent(userId, retryCount + 1);
      }
      console.error('❌ All consent check retries failed (network)');
      setHasConsented(false);
      setConnectionError(true);
    } finally {
      setCheckingConsent(false);
    }
  };

  // Check onboarding status
  const checkOnboardingStatus = async (userId) => {
    setCheckingOnboarding(true);

    const completed = localStorage.getItem(`onboarding_${userId}`);
    if (completed === 'completed') {
      console.log(`📋 Onboarding: completed (localStorage)`);
      setHasCompletedOnboarding(true);
      setCheckingOnboarding(false);
      return;
    }

    // Check if profile is complete (all required fields filled)
    try {
      // Load main profile
      const profileResponse = await authFetch(`${API_BASE_URL}/profile?user_id=${userId}`);

      // Load cycling profile (includes sports_active)
      const cyclingResponse = await authFetch(`${API_BASE_URL}/profile/cycling/${userId}`);

      if (profileResponse.ok) {
        const profileData = await profileResponse.json();
        const cyclingData = cyclingResponse.ok ? await cyclingResponse.json() : null;

        console.log('📋 Checking profile completeness:', { profileData, cyclingData });

        // Check basic fields
        const basicComplete = profileData.name && (profileData.age || profileData.date_of_birth) &&
                             profileData.training_days_per_week &&
                             profileData.avg_weekly_km &&
                             profileData.goal_distance;

        // Check sports selection
        const sportsActive = cyclingData?.sports_active?.split(',') || [];
        const hasSportsSelection = sportsActive.length > 0;

        // Check sport-specific fields
        let sportDetailsComplete = false;
        if (sportsActive.includes('running') && sportsActive.includes('cycling')) {
          // Both sports: need both level and cycling_level
          sportDetailsComplete = profileData.level && cyclingData?.cycling_level;
        } else if (sportsActive.includes('running')) {
          // Running only: need level
          sportDetailsComplete = !!profileData.level;
        } else if (sportsActive.includes('cycling')) {
          // Cycling only: need cycling_level
          sportDetailsComplete = !!cyclingData?.cycling_level;
        }

        const isComplete = basicComplete && hasSportsSelection && sportDetailsComplete;

        console.log(`📋 Profile check: basic=${basicComplete}, sports=${hasSportsSelection}, details=${sportDetailsComplete}, complete=${isComplete}`);

        if (isComplete) {
          console.log(`✅ Onboarding: skipped (profile complete)`);
          localStorage.setItem(`onboarding_${userId}`, 'completed');
          setHasCompletedOnboarding(true);
          setCheckingOnboarding(false);
          return;
        }
      }
    } catch (error) {
      console.log('Could not check profile completeness, will show onboarding:', error);
    }

    console.log(`📋 Onboarding: required (profile incomplete)`);
    setHasCompletedOnboarding(false);
    setCheckingOnboarding(false);
  };

  // Home data (Strava removal C2, 2026-10-21): the loaders that used to run inside the Strava status check.
  // No Strava call of any kind; the Garmin status is read by TrainingSourcesCard itself.
  const loadHomeData = async (userId) => {
    setHomeDataReady(true);
    loadPlannedWeeklyKm(userId);
    loadHomeInsights(userId);
    loadChatCard(userId);
    // Ensure sports_active is always fresh (needed for dual sport home screen)
    try {
      const cycRes = await authFetch(`${API_BASE_URL}/profile/cycling/${userId}`);
      if (cycRes.ok) {
        const cycData = await cycRes.json();
        if (cycData?.sports_active) {
          setProfile(prev => prev ? { ...prev, sports_active: cycData.sports_active } : prev);
        }
      }
    } catch {}
  };

  const loadPlannedWeeklyKm = async (userId) => {
    try {
      const res = await authFetch(`${API_BASE_URL}/weekly-plan/all-active?user_id=${userId}`);
      if (res.ok) {
        const data = await res.json();
        const plans = data.plans || data;
        const runningPlan = Array.isArray(plans)
          ? plans.find(p => (p.sport_type === 'running' || !p.sport_type) && p.is_current_week !== false)
          : null;
        if (runningPlan?.total_km) setPlannedWeeklyKm(runningPlan.total_km);

        // חלץ שלב אימון מתוכן התכנית — Backend כבר חישב נכון
        if (runningPlan?.plan_content || runningPlan?.workouts_json) {
          const content = runningPlan.plan_content
            || (typeof runningPlan.workouts_json === 'string'
                ? JSON.parse(runningPlan.workouts_json)?.plan_text
                : runningPlan.workouts_json?.plan_text) || '';
          // חפש "שלב אימון: X" — רק שמות שלב אמיתיים
          const phaseMatch = content.match(/שלב אימון[:\s]+([^\n|*]+)/i);
          const rawPhase = phaseMatch?.[1]?.trim()
            // נקה מ-markdown bold
            ?.replace(/\*+/g, '')
            ?.trim();
          // פסול אם הטקסט מכיל שם מרוץ / "יעד" / "מרוץ"
          const PHASE_KEYWORDS = ['חידוד', 'טייפר', 'יכולות', 'בסיס', 'שמירה', 'peak', 'build', 'base'];
          const isValidPhase = rawPhase && PHASE_KEYWORDS.some(k => rawPhase.toLowerCase().includes(k));
          if (isValidPhase) {
            const phaseTxt = rawPhase;
            const emoji = phaseTxt.includes('חידוד') ? '🎯'
              : phaseTxt.includes('טייפר') ? '🏁'
              : phaseTxt.includes('יכולות') ? '💪'
              : phaseTxt.includes('בסיס') ? '🏗️' : '✅';
            const color = phaseTxt.includes('חידוד') ? '#34d399'
              : phaseTxt.includes('טייפר') ? '#f87171'
              : phaseTxt.includes('יכולות') ? '#60a5fa'
              : phaseTxt.includes('בסיס') ? '#a78bfa' : '#94a3b8';
            const desc = phaseTxt.includes('חידוד') ? 'אימונים איכותיים וחדים'
              : phaseTxt.includes('טייפר קל') ? 'מתחילים להוריד הילוך'
              : phaseTxt.includes('טייפר') ? 'הפחתת עומס לפני המרוץ'
              : phaseTxt.includes('יכולות') ? 'עבודה על מהירות וסף'
              : phaseTxt.includes('בסיס') ? 'בניית נפח וסיבולת'
              : '';
            setWeeklyPlanPhase({ phase: phaseTxt, emoji, color, description: desc });
          }
        }
      }
    } catch {}
  };

  const _applyGenerateResult = (genData) => {
    if (genData.success) {
      saveHomeInsights({
        reinforcement:      genData.reinforcement,
        recommendations:    genData.recommendations,
        indicators:         genData.indicators,
        last_workout:       genData.last_workout,
        top4:               genData.top4,
        praise:             genData.praise,
        trend_insight_card: genData.trend_insight_card || null,
        generated_at:       new Date().toISOString(),
      });
    }
  };

  // Home cards must reflect a plan change IMMEDIATELY (fix 2026-08-28): a
  // chat/tool plan update dispatches 'plansUpdated', but only the WeeklyPlan
  // screen listened — the next-workout card, chat card and weekly-km kept
  // stale data until a full reload. Refresh every home consumer of
  // active-plan data on the same event. No full-page reload.
  useEffect(() => {
    if (!user) return;
    const onPlansUpdated = () => {
      try {
        loadWorkoutTrend(user.uid);
        loadChatCard(user.uid);
        loadPlannedWeeklyKm(user.uid);
      } catch (e) {
        console.warn('plansUpdated home refresh failed:', e);
      }
    };
    window.addEventListener('plansUpdated', onPlansUpdated);
    return () => window.removeEventListener('plansUpdated', onPlansUpdated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Chat-card state (handoff_chat_card): C1/C2 + up to two server-derived
  // suggested questions. Degrades silently — the card renders as a plain
  // chat entry when this fails or is still loading.
  const loadChatCard = async (userId) => {
    setChatCardLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/chat/card-state?user_id=${userId}`);
      if (res.ok) setChatCard(await res.json());
    } catch { /* plain entry */ }
    finally { setChatCardLoading(false); }
  };

  const loadHomeInsights = async (userId) => {
    if (insightsLoadingRef.current) return;
    insightsLoadingRef.current = true;
    let cachedInsights = null;
    try {
      const res = await authFetch(`${API_BASE_URL}/insights?user_id=${userId}`);
      if (res.ok) {
        const data = await res.json();
        if (data.insights) {
          cachedInsights = data.insights;
          saveHomeInsights(data.insights);
        }
      }
    } catch {}

    // Fire workout-trend fetch in parallel with the generate call (do not await here)
    loadWorkoutTrend(userId);

    // Stale-while-revalidate: generate in background if insights are older than 2 hours.
    // Keeps last_workout / next_workout fresh on every open without calling Gemini too often.
    const _genAgeMin = cachedInsights?.generated_at
      ? Math.round((Date.now() - new Date(cachedInsights.generated_at)) / 60000)
      : 999;
    // 2026-10-27: the Graphite Home never triggers an AI call on load — this generation feeds only the previous
    // Home's insight cards (homeInsights); the daily cron refresh is unchanged.
    if (!HOME_GRAPHITE_ENABLED && (!cachedInsights || _genAgeMin > 120)) {
      try {
        const genRes = await authFetch(`${API_BASE_URL}/insights/generate?user_id=${userId}`, {
          method: 'POST',
        });
        if (genRes.ok) {
          const genData = await genRes.json();
          _applyGenerateResult(genData);
        }
      } catch {}
    }
    insightsLoadingRef.current = false;
  };

  // ── Workout-trend fetch (truly parallel — fires after /insights returns, does not wait for generate) ──
  // Stored separately so it starts while generate is running
  const loadWorkoutTrend = async (userId) => {
    const myId = ++wtFetchIdRef.current;
    try {
      const trendRes = await authFetch(`${API_BASE_URL}/insights/workout-trend?user_id=${userId}`);
      if (trendRes.ok) {
        const trendData = await trendRes.json();
        // Only save if no newer fetch has started since we were called
        if (trendData.available && myId === wtFetchIdRef.current) saveWorkoutTrend(trendData);
      }
    } catch {}
  };

  // "צפה בניתוח": open the SAVED deep analysis of the card's workout in a viewer (read only — no /chat, no
  // /chat/analyze-workout, so never a new generation). date: the workout the backend just named (already_analyzed);
  // a different date than the card's ref drops the ref's activity id. Not found / error -> a message, nothing else.
  const openSavedDeepAnalysis = async (trend, date) => {
    if (!user) return;
    const ref = trend?.deep_analysis_ref;
    const t = date && ref?.date !== date ? { deep_analysis_ref: { date } } : trend;
    setDeepView({ loading: true, text: '', date: date || ref?.date || trend?.today_workout?.date });
    const out = await fetchSavedDeepAnalysis(authFetch, API_BASE_URL, user.uid, t);
    if (out.status === 'ok') {
      setDeepView({ loading: false, text: out.text, date: out.date });
      return;
    }
    setDeepView(null);
    showToast(out.status === 'not_found' ? 'לא מצאתי את הניתוח השמור לאימון הזה — אפשר לשאול את המאמן עליו בצ׳אט'
                                         : 'לא ניתן לפתוח את הניתוח כרגע — נסה שוב בעוד רגע', 'info', 3500);
  };

  // Deep analysis of the Home card's workout — click only (never on load). POST /chat/analyze-workout claims the
  // workout's ONE analysis and returns the message the chat sends through the full pipeline (pendingAnalysis_).
  // Shared by the Graphite Home and the previous Home (insightProps).
  const deepInFlightRef = useRef(false);
  const startDeepAnalysis = async () => {
    if (!user || deepAnalyzing || deepInFlightRef.current) return;
    deepInFlightRef.current = true;          // a second click before the re-render never sends a second request
    setDeepAnalyzing(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/chat/analyze-workout?user_id=${user.uid}`, { method: 'POST' });
      const data = await res.json();
      if (data.status === 'ok') {
        // Send the deep-analysis request through the FULL chat pipeline
        // (splits + profile + history) — that's what makes it deep.
        try { localStorage.setItem(`pendingAnalysis_${user.uid}`, data.message); } catch {}
        await loadWorkoutTrend(user.uid);   // button flips to "נותח ✓"
        setCurrentPage('chat');
      } else if (data.status === 'already_analyzed') {
        // ONE deep analysis per workout (2026-10-23): open the saved one, never a new generation
        await loadWorkoutTrend(user.uid);
        openSavedDeepAnalysis(workoutTrend, data.date);
      } else if (data.status === 'exhausted') {
        showToast('ניתוח העומק לאימון הזה לא הושלם — אפשר לשאול את המאמן עליו בצ׳אט', 'info', 4000);
        await loadWorkoutTrend(user.uid);
      } else if (data.status === 'analyzing') {
        // another click / device already started this workout's analysis (one Pro call)
        showToast('הניתוח לעומק כבר בדרך — הוא יופיע בצ׳אט', 'info', 3000);
      } else if (data.status === 'quota_exhausted') {
        showToast('נגמרו ההודעות היומיות — נסה מחר', 'error', 3000);
      } else {
        showToast('לא ניתן לנתח כרגע', 'error', 3000);
      }
    } catch (e) {
      showToast('שגיאה בניתוח המעמיק', 'error', 3000);
    } finally {
      deepInFlightRef.current = false;
      setDeepAnalyzing(false);
    }
  };

  // "מוכן לחזור" of the PREVIOUS Home banner (HOME_GRAPHITE_ENABLED=false rollback only): the existing Return to Run
  // start — POST /profile/injury-return, the profile re-read, the rebuild the server recorded. The Graphite Home never
  // calls it (owner 2026-11-02): its "עדכון מצב פציעה" only opens the profile's injury card.
  const injuryReturnInFlightRef = useRef(false);
  const startInjuryReturn = async () => {
    const uid = user?.uid;
    if (!uid || injuryReturnInFlightRef.current) return;
    injuryReturnInFlightRef.current = true;          // a second click before the re-render never sends a second request
    try {
      const res = await authFetch(`${API_BASE_URL}/profile/injury-return?user_id=${uid}`, { method: 'POST' });
      if (res.ok) {
        const pr = await authFetch(`${API_BASE_URL}/profile?user_id=${uid}`);
        if (pr.ok) { const pd = await pr.json(); setProfile(pd); }
        // 2026-10-30: the return start is a real profile change — the four weeks follow the ramp now
        showToast('מעדכן את התכנית לחזרה ההדרגתית…', 'info', 4000);
        const out = await runPlanRebuild(uid, { reason: 'injury' });
        if (out.kind !== 'none') showToast(out.message, out.ok ? 'success' : 'error', out.ok ? 5000 : 9000);
      } else {
        showToast('לא ניתן להתחיל את החזרה כרגע — נסה שוב בעוד רגע', 'error', 4000);
      }
    } catch (e) {
      console.warn('injury-return failed:', e);
      showToast('לא ניתן להתחיל את החזרה כרגע — נסה שוב בעוד רגע', 'error', 4000);
    } finally {
      injuryReturnInFlightRef.current = false;
    }
  };

  // ── helpers ──────────────────────────────────────────────
  const paceToSec = p => { if (!p) return 0; const [m,s] = String(p).split(':').map(Number); return (m||0)*60+(s||0); };
  const secToPace = s => `${Math.floor(s/60)}:${String(Math.round(s%60)).padStart(2,'0')}`;

  const getTrainingPhase = (daysToRace, raceDistance, raceName) => {
    // Race day (or just past) — separate label so the user sees 'today
    // is the race' instead of 'maintenance' (the fallback that fired
    // when daysToRace was falsy). Also handles daysToRace=null after
    // a freshly-passed race when the races list hasn't been updated.
    if (daysToRace === 0) {
      const _name = (raceName || '').trim();
      const _desc = _name
        ? `בהצלחה ב${_name}! המאמן מאחוריך 🚀`
        : 'הצלחה במרוץ! המאמן מאחוריך 🚀';
      return { phase: 'יום תחרות', emoji: '🏁', color: '#f87171', description: _desc };
    }
    if (!daysToRace || !raceDistance) return { phase: 'שמירת כושר', emoji: '✅', color: '#94a3b8', description: 'אימון חופשי ללא מרוץ' };
    const dist = String(raceDistance).toLowerCase().trim();
    // זיהוי רחב — תומך ב-"10 ק"מ", "10k", "10km", "10K", "10"
    const is5k = /^5$/.test(dist) || dist.includes('5k') || dist.includes('5km') || dist.includes('5 ק');
    const is10k = /^10$/.test(dist) || dist.includes('10k') || dist.includes('10km') || dist.includes('10 ק');
    const isHalf = dist.includes('חצי') || dist.includes('half') || dist.includes('21') || dist.includes('half_marathon');
    const isMarathon = (dist.includes('מרתון') || dist.includes('marathon') || dist.includes('42')) && !isHalf;
    console.log(`📊 Phase debug: dist="${dist}" is5k=${is5k} is10k=${is10k} isHalf=${isHalf} isMara=${isMarathon} days=${daysToRace}`);
    const d = daysToRace;
    if ((is5k && d<=7)||(is10k && d<=10)||(isHalf && d<=14)||(isMarathon && d<=14))
      return { phase: 'טייפר', emoji: '🏁', color: '#f87171', description: 'הפחתת עומס לפני המרוץ' };
    if (isMarathon && d>=15 && d<=21)
      return { phase: 'טייפר קל', emoji: '😌', color: '#f59e0b', description: 'מתחילים להוריד הילוך' };
    if ((is5k && d>=8 && d<=14)||(is10k && d>=11 && d<=21)||(isHalf && d>=15 && d<=42)||(isMarathon && d>=22 && d<=56))
      return { phase: 'חידוד ושיא', emoji: '🎯', color: '#34d399', description: 'אימונים איכותיים וחדים' };
    if ((is5k && d>=15 && d<=35)||(is10k && d>=22 && d<=42)||(isHalf && d>=43 && d<=70)||(isMarathon && d>=57 && d<=84))
      return { phase: 'בניית יכולות', emoji: '💪', color: '#60a5fa', description: 'עבודה על מהירות וסף' };
    return { phase: 'בניית בסיס', emoji: '🏗️', color: '#a78bfa', description: 'בניית נפח וסיבולת' };
  };

  const calcWeeklyStats = (acts) => {
    const getWeekStart = (d) => { const x = new Date(d); x.setDate(x.getDate()-x.getDay()); x.setHours(0,0,0,0); return x; };
    const wsThis = getWeekStart(new Date());
    const wsLast = new Date(wsThis); wsLast.setDate(wsLast.getDate()-7);
    const getD = a => new Date((a.date||a.startTimeLocal||a.start_date_local||'').slice(0,10));
    const tw = acts.filter(a => getD(a) >= wsThis);
    const lw = acts.filter(a => { const d=getD(a); return d>=wsLast && d<wsThis; });
    const sum = arr => arr.reduce((s,a) => s+(parseFloat(a.distance)||0), 0);
    const thisWeekKm = Math.round(sum(tw)*10)/10;
    const lastWeekKm = Math.round(sum(lw)*10)/10;
    const diff = parseFloat((thisWeekKm-lastWeekKm).toFixed(1));
    const consistency = lastWeekKm > 0 ? Math.round(((thisWeekKm-lastWeekKm)/lastWeekKm)*100) : null;
    return { thisWeekKm, lastWeekKm, diff, consistency };
  };

  // ── AI Coach Insights — full inline section on home screen ──────────────
  const handleHomeDismiss = async (type) => {
    setHomeDismissed(prev => [...prev, type]);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://claude-coach-backend-1.onrender.com';
      await authFetch(`${API_BASE_URL}/insights/dismiss?user_id=${user?.uid}&insight_type=${type}`, { method: 'POST' });
    } catch {}
  };

  const handleForceRefreshInsights = async () => {
    if (insightsRefreshing || !user?.uid) return;
    setInsightsRefreshing(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://claude-coach-backend-1.onrender.com';
      // 1. Backfill plan history (populates current week from plan JSON)
      await authFetch(`${API_BASE_URL}/plan-history/backfill?user_id=${user.uid}`, { method: 'POST' }).catch(() => {});
      // 2. Clear cached trend and fetch fresh from backend
      localStorage.removeItem('wt_cache');
      setWorkoutTrend(null);
      await loadWorkoutTrend(user.uid);
      showToast('✅ ניתוח עודכן', 'success', 3000);
    } catch {
      showToast('❌ שגיאה בעדכון ניתוח', 'error', 3000);
    }
    setInsightsRefreshing(false);
  };

  const buildCoachInsightData = () => {
    const allActs = Array.isArray(lastActivity) ? lastActivity : [];
    const getDate = a => (a.date || a.startTimeLocal || a.start_date_local || '').slice(0, 10);

    // פונקציה אחת לחישוב תחילת שבוע קלנדרי (ראשון) — משמשת בכל חישוב
    const getWeekStart = (date) => {
      const d = new Date(date);
      d.setDate(d.getDate() - d.getDay()); // 0=ראשון
      d.setHours(0, 0, 0, 0);
      return d;
    };
    const thisWeekStart = getWeekStart(new Date());
    const lastWeekStart = new Date(thisWeekStart);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);

    // פילטור לפי sport_type לפני כל חישוב
    const sportsActive = (profile?.sports_active || 'running').toLowerCase();
    const isCycling = CYCLING_ENABLED && sportsActive.includes('cycling') && !sportsActive.includes('running');
    const acts = allActs.filter(a => {
      const t = (a.activity_type || a.type || a.sport_type || '').toLowerCase();
      return isCycling ? (t.includes('ride') || t.includes('cycl')) : (t.includes('run') || t === '');
    });

    // ① ק"מ שבועי קלנדרי: שבוע נוכחי vs שבוע שעבר
    const thisWeek = acts.filter(a => new Date(getDate(a)) >= thisWeekStart);
    const prevWeek = acts.filter(a => { const d = new Date(getDate(a)); return d >= lastWeekStart && d < thisWeekStart; });
    const thisKm = parseFloat(thisWeek.reduce((s, a) => s + (parseFloat(a.distance) || 0), 0).toFixed(1));
    const prevKm = parseFloat(prevWeek.reduce((s, a) => s + (parseFloat(a.distance) || 0), 0).toFixed(1));

    // ② עקביות: ממוצע 4 שבועות אחרונים vs 4 שבועות לפניהם (קלנדרי)
    const weekOf = (dateStr) => getWeekStart(new Date(dateStr)).getTime();
    const kmByWeek = acts.reduce((acc, a) => {
      const w = weekOf(getDate(a));
      acc[w] = (acc[w] || 0) + (parseFloat(a.distance) || 0);
      return acc;
    }, {});
    const nowWeekTs = thisWeekStart.getTime();
    const weekMs = 7 * 86400000;
    const recent4 = Object.entries(kmByWeek).filter(([w]) => nowWeekTs - Number(w) < 4 * weekMs).map(([,v]) => v);
    const prev4   = Object.entries(kmByWeek).filter(([w]) => { const diff = nowWeekTs - Number(w); return diff >= 4 * weekMs && diff < 8 * weekMs; }).map(([,v]) => v);
    const avgRecent = recent4.length ? recent4.reduce((s,v) => s+v, 0) / recent4.length : 0;
    const avgPrev   = prev4.length   ? prev4.reduce((s,v) => s+v, 0)   / prev4.length   : 0;
    const consistency = avgPrev > 0 ? Math.round(((avgRecent - avgPrev) / avgPrev) * 100) : null;

    const isPositive = consistency === null ? true : consistency >= 0;
    const color = isPositive ? '#34d399' : '#f59e0b';
    const prefix = consistency !== null && consistency > 0 ? '+' : '';
    const consistencyMsg = consistency !== null
      ? isPositive ? 'מעל הממוצע שלך' : 'מתחת לממוצע שלך'
      : 'אין נתון להשוואה';

    // ③ diff ק"מ שבועי
    const kmDiff = prevKm > 0 ? parseFloat((thisKm - prevKm).toFixed(1)) : null;
    const kmDiffText = kmDiff === null ? null
      : kmDiff === 0 ? 'זהה לשבוע קודם'
      : `${kmDiff > 0 ? '+' : ''}${kmDiff} ק"מ משבוע קודם`;
    const kmDiffColor = kmDiff === null || kmDiff >= 0 ? '#34d399' : '#f59e0b';

    console.log(`📊 Coach Insight: thisWeek=${thisKm}km, prevWeek=${prevKm}km, consistency=${consistency}%`);

    const hasActivity = thisWeek.length > 0;
    const raceTag = daysToRace !== null && daysToRace > 0 && daysToRace <= 60
      ? { days: daysToRace, name: _nearestRace?.name || 'המרוץ' } : null;
    return { thisKm, prevKm, consistency, isPositive, color, prefix, consistencyMsg, kmDiff, kmDiffText, kmDiffColor, hasActivity, raceTag, noData: acts.length === 0 };
  };

  // Check user type (B2C, B2B, or Coach)
  const fetchWithTimeout = (url, ms = 8000) => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), ms);
    return authFetch(url, { signal: controller.signal }).finally(() => clearTimeout(id));
  };

  const checkUserType = async (userId, emailOverride = null) => {
    setCheckingUserType(true);
    try {
      const coachRes = await fetchWithTimeout(`${API_BASE_URL}/coach/profile?user_id=${userId}`);
      if (coachRes.ok) {
        const coachData = await coachRes.json();
        if (coachData.is_coach) {
          setUserType('coach');
          console.log('👔 User type: COACH');
          setCheckingUserType(false);
          return 'coach';
        }
      }

      const athleteRes = await fetchWithTimeout(`${API_BASE_URL}/athlete/my-coach?user_id=${userId}`);
      if (athleteRes.ok) {
        const athleteData = await athleteRes.json();
        if (athleteData.has_coach) {
          // Bypass list (2026-06-11) — admin + test athlete keep B2C-style
          // chat access even when attached to a coach for QA testing.
          // Matches backend gate in /chat (main.py _CHAT_BYPASS_EMAILS).
          // emailOverride is passed by the auth flow because React `user`
          // state isn't yet populated when checkUserType fires.
          const _CHAT_BYPASS_EMAILS = ['liorpar@gmail.com', 'liortestai@gmail.com'];
          const _userEmailLc = (emailOverride || user?.email || '').toLowerCase().trim();
          if (_CHAT_BYPASS_EMAILS.includes(_userEmailLc)) {
            setUserType('b2c');  // ← admin/test sees the open-chat UI
            setMyCoach(athleteData);  // ← but still tracks the coach link
            console.log(`🔓 User type: B2B → bypassed to B2C for ${_userEmailLc} (admin/test)`);
            setCheckingUserType(false);
            return 'b2c';
          }
          setUserType('b2b');
          setMyCoach(athleteData);
          setPendingInviteCode(null);
          localStorage.removeItem('pending_invite_code');
          console.log(`🏃 User type: B2B (Coach: ${athleteData.coach_name})`);
          setCheckingUserType(false);
          return 'b2b';
        }
      }

      setUserType('b2c');
      console.log('🏃 User type: B2C (independent)');
      setCheckingUserType(false);
      return 'b2c';

    } catch (error) {
      console.log('Could not check user type, defaulting to B2C:', error.name);
      setUserType('b2c');
      setCheckingUserType(false);
      return 'b2c';
    }
  };

  // Listen to auth state
  useEffect(() => {
    console.log('LOADED: Train Smart AI v2.8 - Usage Display');

    // Pre-warm Render free-tier: fire a /health ping immediately on app load.
    // If the backend has been sleeping (>15 min idle), this triggers the
    // cold start while the user is still seeing the loading screen / login,
    // so by the time the actual API calls begin Render is already warm.
    // Fire-and-forget — failure is fine, the regular retry logic covers it.
    fetch(`${API_BASE_URL}/health`, { cache: 'no-store' }).catch(() => {});

    const unsubscribe = onAuthChange(async (firebaseUser) => {
      if (firebaseUser) {
        console.log('✅ User logged in:', firebaseUser.email);
        // Restore cached insights only for the SAME user — prevents cross-user data leakage
        const _lastUid = localStorage.getItem('_last_active_uid');
        if (_lastUid === firebaseUser.uid) {
          const _ct = _lsGet('wt_cache');
          const _ci = _lsGet('hi_cache');
          if (_ct) setWorkoutTrend(_ct);
          if (_ci) setHomeInsights(_ci);
        } else {
          localStorage.removeItem('wt_cache');
          localStorage.removeItem('hi_cache');
        }
        localStorage.setItem('_last_active_uid', firebaseUser.uid);
        setUser(firebaseUser);

        // המתן לסיום בדיקת החיבור לפני כל קריאות API (ref-based, closure-safe)
        await new Promise(resolve => {
          const check = () => {
            if (connectionReadyRef.current) { resolve(); return; }
            setTimeout(check, 100);
          };
          check();
        });
        
        if (firebaseUser.email) {
          try {
            await authFetch(`${API_BASE_URL}/profile/email?user_id=${firebaseUser.uid}&email=${encodeURIComponent(firebaseUser.email)}`, {
              method: 'PUT'
            });
          } catch (e) {
            console.log('⚠️ Could not save email to profile');
          }
        }
        
        // Only the consent + profile checks gate the app-shell render.
        // loadMessageUsage / loadNextRace are home-screen widgets — if
        // the backend is slow, the home tab will refetch them when the
        // user lands there. Awaiting them here was the second source
        // of the "stuck on loading" symptom: even after the cache-
        // first profile rendered, Promise.all blocked on these two
        // fetches timing out (~8s each × auto-retries inside authFetch).
        await Promise.all([
          checkUserConsent(firebaseUser.uid),
          checkProfileComplete(firebaseUser.uid),
        ]);
        // Fire-and-forget the home-screen widgets. The home-tab
        // useEffect (lines ~770-790) will refetch them when the user
        // actually navigates to home, so we don't lose data — we just
        // don't block the splash screen on them.
        loadMessageUsage(firebaseUser.uid).catch(() => {});
        loadNextRace(firebaseUser.uid).catch(() => {});

        // Run the user-type + onboarding cascade ONCE per uid. onAuthChange re-fires
        // on token refresh (frequent during the long onboarding plan-gen); re-running
        // this flips checkingUserType/checkingOnboarding back to true → unmounts the
        // OnboardingWizard → Step6 re-fires welcome-token + regenerate-all in a loop.
        if (authCascadeUidRef.current !== firebaseUser.uid) {
          authCascadeUidRef.current = firebaseUser.uid;
          const userTypeResult = await checkUserType(firebaseUser.uid, firebaseUser.email);
          if (userTypeResult !== 'b2b') {
            await checkOnboardingStatus(firebaseUser.uid);
          } else {
            console.log('📋 B2B user - skipping onboarding');
            setHasCompletedOnboarding(true);
            setCheckingOnboarding(false);
          }
        } else {
          // Cascade already ran for this uid — the checking flags MUST be off. A transient
          // null auth event flips checkingUserType back to true; without this, the re-auth
          // of the SAME uid skips the cascade and nothing ever turns the flag off again →
          // the app hangs on 'בודק חשבון…' until a full refresh (real-user bug 2026-08-24).
          setCheckingUserType(false);
          setCheckingOnboarding(false);
        }

        loadHomeData(firebaseUser.uid);

        // Context flag: check for a recent workout awaiting "how did you feel?"
        // days=7 so existing test data (last 7 days) also triggers it
        authFetch(`${API_BASE_URL}/plan-history/pending-context?user_id=${firebaseUser.uid}&days=7`)
          .then(r => r.json()).then(d => {
            if (d.pending) setPendingContextWorkout(d.pending);
          }).catch(() => {});

        // F5-resume (2026-06-04): if a plan generation is in flight
        // for this user (e.g. user F5'd mid-cycling-generation), pull
        // the live job and re-attach the global progress banner +
        // quiet polling. Without this the banner is in-memory only,
        // so an F5 destroys the visible state even though the BG
        // task is still running server-side.
        resumeInFlightPlanGeneration(firebaseUser.uid).catch(err => {
          console.log('Plan-status resume failed (non-critical):', err);
        });

        // 🔔 רענון אוטומטי של FCM token — מתקן את הבעיה שהתראות הפסיקו לעבוד יום אחר
        refreshPushTokenIfNeeded(firebaseUser.uid).catch(err => {
          console.log('Push token refresh failed (non-critical):', err);
        });
      } else {
        console.log('❌ No user logged in');
        setUser(null);
        setWorkoutTrend(null);
        setHomeInsights(null);
        setProfileComplete(null);
        setProfile(null);
        setNextRace(null);
        setHasConsented(false);
        setCheckingConsent(false);
        setConsentVersionMismatch(false);
        setHasCompletedOnboarding(false);
        setCheckingOnboarding(false);
        setHomeDataReady(false);
        setUserType('b2c');
        setCheckingUserType(true);
        // A null auth event must also clear the cascade guard: the next sign-in (even of
        // the same uid) needs the full user-type/onboarding cascade to run again — the
        // stuck-splash trap fixed above relies on this reset for the clean path.
        authCascadeUidRef.current = null;
        setMyCoach(null);
        // 🆕 Reset usage
        setDailyMessageCount(0);
        setDailyLimit(10);
        setMessagesRemaining(10);
      }
      setLoading(false);
      // Hide loading screen
      const loadingScreen = document.getElementById('loading-screen');
      if (loadingScreen) {
        loadingScreen.style.opacity = '0';
        setTimeout(() => loadingScreen.remove(), 300);
      }
    });

    return () => unsubscribe();
  }, []);

  // Refresh usage + next race + home data when returning to home
  useEffect(() => {
    if (user && currentPage === 'home') {
      loadMessageUsage(user.uid);
      loadNextRace(user.uid);
      loadHomeData(user.uid);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, user]);

  // ── Android / browser Back (2026-10-20) ─────────────────────────
  // Screens are React state, so Back needs an entry of our own (utils/backNav). An inner main screen holds ONE
  // 'page' entry: Back returns Home through the unsaved-changes guard; Home holds none, so Back there exits
  // normally. The entry is created with a user activation (a tap) or deferred to the next interaction — never
  // on load or inside popstate, which Chrome / the Android PWA skip (the old "sentinel" did exactly that, and
  // Back sometimes closed the app). Login, consent, onboarding, its loader and the coach-invite screen keep
  // the browser's normal Back. Modals do not add entries in this release: Back with a modal open behaves as
  // before (returns Home; an unsaved Profile form still asks first through its 'guard' entry).
  const currentPageRef = useRef(currentPage);
  useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);
  const _onboardingShown = hasConsented && !hasCompletedOnboarding && !checkingOnboarding && !checkingUserType
    && userType !== 'b2b';
  const backNavMainScreen = !!user && !loading && !checkingConsent && profileComplete !== null && !checkingUserType
    && !connectionError && !consentVersionMismatch && hasConsented && !_onboardingShown
    && !(inviteCode && hasCompletedOnboarding) && currentPage !== 'onboarding-loading';
  useBackEntry(backNavMainScreen && currentPage !== 'home', 'page', () => {
    if (currentPageRef.current === 'home') return 'done';
    if (!confirmNavigation()) return 'stay';     // unsaved changes: the existing confirmation; "stay" keeps them
    setCurrentPage('home');
    return 'done';
  });
  // ── Cross-screen navigation: Profile guardrail → Races editor ──
  // Profile.jsx fires 'navigate-to-race-edit' when the user accepts
  // a guardrail suggestion that originated from a race in their
  // calendar. We stash the race id + suggested distance in
  // localStorage (60s TTL via timestamp) and flip to the Races tab;
  // Races.jsx picks the handoff up on mount and opens its edit
  // modal pre-filled with the suggestion.
  useEffect(() => {
    const onNav = (e) => {
      const { raceId, suggestedDistance } = e?.detail || {};
      if (raceId) {
        try {
          localStorage.setItem('pending_race_edit', JSON.stringify({
            raceId,
            suggestedDistance: suggestedDistance || null,
            ts: Date.now(),
          }));
        } catch {}
      }
      setCurrentPage('races');
    };
    window.addEventListener('navigate-to-race-edit', onNav);
    return () => window.removeEventListener('navigate-to-race-edit', onNav);
  }, []);

  // ── Global plan-regenerate banner ───────────────────────────────
  // Profile.jsx fires the actual generate-v2 calls when the user
  // accepts the post-save toast. The fetches take ~30s (current +
  // next week, sequential). If the user navigates AWAY from the
  // profile screen during that window, the local in-Profile spinner
  // disappears and they have no idea work is still in flight. This
  // banner is a top-bar that stays visible on EVERY screen until the
  // 'plan-regen-done' event fires, regardless of where the user is.
  // Persist banner state across F5 in sessionStorage so a refresh
  // mid-batch doesn't drop the user back to a blank screen while
  // resumeInFlightPlanGeneration races to detect the in-flight job.
  // User report 2026-06-07: F5 made the banner disappear even though
  // generation was still active.
  const [planRegenStatus, setPlanRegenStatus] = useState(() => {
    try {
      const raw = window.sessionStorage.getItem('planRegenStatus');
      if (raw) return JSON.parse(raw);
    } catch {}
    return null;
  });
  // Elapsed-minutes counter for the running banner. Updates ONCE per
  // minute (not every second) so the banner text doesn't churn — user
  // report 2026-06-04: 'רק קטע מתנגן … לא מפסיק רענון מסך כל שניה'.
  // The user thought the per-second re-render WAS a screen refresh.
  // Minute-level granularity gives enough progress signal ("4 דקות
  // עברו") without animating the banner.
  const [planRegenMinutes, setPlanRegenMinutes] = useState(0);
  useEffect(() => {
    if (!planRegenStatus || planRegenStatus.phase !== 'running') {
      setPlanRegenMinutes(0);
      return;
    }
    const startedAt = Date.now();
    const id = setInterval(() => {
      setPlanRegenMinutes(Math.floor((Date.now() - startedAt) / 60000));
    }, 60000);
    return () => clearInterval(id);
  }, [planRegenStatus]);
  // Persist planRegenStatus to sessionStorage so F5 during the long
  // regenerate keeps the progress banner visible. ONLY persist the
  // 'running' phase — 'done' and 'error' are ephemeral notifications
  // dismissed by a setTimeout in the event handler. Persisting them
  // across reloads caused the success banner to get stuck at the top
  // of the screen after refresh (user incident 2026-06-08), because
  // the restored state has no timer to clear it.
  useEffect(() => {
    try {
      if (planRegenStatus && planRegenStatus.phase === 'running') {
        window.sessionStorage.setItem('planRegenStatus', JSON.stringify(planRegenStatus));
      } else {
        window.sessionStorage.removeItem('planRegenStatus');
      }
    } catch {}
  }, [planRegenStatus]);
  useEffect(() => {
    const onStart = (e) => {
      const changedFields = (e?.detail?.changedFields) || [];
      setPlanRegenStatus({ phase: 'running', changedFields });
    };
    const onDone  = (e) => {
      // A chat edit that ended WITHOUT a write on purpose (blocked by a safety rule, valid
      // alternatives offered instead) is neither "plan ready" nor "generation failed" — the chat
      // message explains it; the banner just closes.
      if (e?.detail?.dismiss) {
        setPlanRegenStatus(null);
        return;
      }
      const ok = !!(e?.detail?.success);
      if (ok) {
        // Notify WeeklyPlan to re-fetch — it may still be mounted showing
        // stale data from before the regen started. The plansUpdated handler
        // in WeeklyPlan calls fetchAllPlans() after a 2s delay which refreshes
        // both state and localStorage cache.
        window.dispatchEvent(new CustomEvent('plansUpdated', { detail: { source: 'plan-regen-done' } }));
      }
      setPlanRegenStatus(prev => ({
        phase: ok ? 'done' : 'error',
        changedFields: prev?.changedFields || [],
      }));
      // Auto-clear after 10 seconds so the user has enough time to
      // read the success/failure message. User report 2026-06-07:
      // 4 seconds was too fast — they saw the banner disappear and
      // wondered if anything had happened.
      setTimeout(() => setPlanRegenStatus(null), 10000);
    };
    window.addEventListener('plan-regen-start', onStart);
    window.addEventListener('plan-regen-done',  onDone);
    return () => {
      window.removeEventListener('plan-regen-start', onStart);
      window.removeEventListener('plan-regen-done',  onDone);
    };
  }, []);

  // Check if user profile is complete - with retry and cache
  const checkProfileComplete = async (userId, retryCount = 0) => {
    console.log('🔍 Checking profile for user:', userId);

    // F5 fast-path (2026-06-04): if we have a cached profile, surface
    // it IMMEDIATELY so the app exits the loading screen even when the
    // backend is slow (cold start, or an active plan generation eating
    // the main event loop). Then refresh in the background so the
    // cache stays fresh. Without this the user sees ~26s of "טוען..."
    // because checkProfileComplete does 3 retries × ~8s timeout before
    // falling back to cache.
    if (retryCount === 0) {
      const cached = localStorage.getItem(`profile_${userId}`);
      if (cached) {
        try {
          const cachedProfile = JSON.parse(cached);
          console.log('📦 Using cached profile immediately (F5 fast-path)');
          setProfile(cachedProfile);
          setProfileComplete(!!(cachedProfile.name && (cachedProfile.age || cachedProfile.date_of_birth)));
          // Background refresh — best-effort, no retries, no await.
          // If it succeeds, the cache + state get updated; if it fails,
          // the cached state we just rendered is fine.
          (async () => {
            try {
              const r = await authFetch(`${API_BASE_URL}/profile?user_id=${userId}`);
              if (r.ok) {
                const fresh = await r.json();
                if (!fresh.sports_active) {
                  try {
                    const cr = await authFetch(`${API_BASE_URL}/profile/cycling/${userId}`);
                    if (cr.ok) {
                      const cd = await cr.json();
                      if (cd?.sports_active) fresh.sports_active = cd.sports_active;
                    }
                  } catch {}
                }
                setProfile(fresh);
                localStorage.setItem(`profile_${userId}`, JSON.stringify({ ...fresh, date_of_birth: undefined }));   // no full birth date on the device
                setProfileComplete(!!(fresh.name && (fresh.age || fresh.date_of_birth)));
              }
            } catch (_) { /* silent — cached state already rendered */ }
          })();
          return;
        } catch (_) { /* cache parse failed — fall through to network */ }
      }
    }

    try {
      const response = await authFetch(`${API_BASE_URL}/profile?user_id=${userId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      console.log('📡 Profile API response status:', response.status);

      if (response.ok) {
        const profileData = await response.json();
        console.log('📋 Profile data received:', profileData);

        // Merge sports_active from cycling profile if missing
        if (!profileData.sports_active) {
          try {
            const cycRes = await authFetch(`${API_BASE_URL}/profile/cycling/${userId}`);
            if (cycRes.ok) {
              const cycData = await cycRes.json();
              if (cycData?.sports_active) profileData.sports_active = cycData.sports_active;
            }
          } catch {}
        }
        setProfile(profileData);

        // 🔧 FIX: Cache profile in localStorage for cross-device reliability
        localStorage.setItem(`profile_${userId}`, JSON.stringify({ ...profileData, date_of_birth: undefined }));   // no full birth date on the device

        // ✅ Only require basic fields - sports-specific fields are optional
        const requiredFields = ['name', 'age'];
        const missingFields = requiredFields.filter(field => (field === 'age'
          ? !(profileData.age || profileData.date_of_birth)   // date of birth counts as the age (2026-10-19)
          : !profileData[field]));

        if (missingFields.length > 0) {
          console.log('⚠️ Profile incomplete. Missing:', missingFields);
          setProfileComplete(false);
        } else {
          console.log('✅ Profile complete');
          setProfileComplete(true);
        }
      } else if (response.status === 404) {
        console.log('⚠️ No profile found (404) - treating as incomplete');
        setProfileComplete(false);
        setProfile(null);
      } else if (retryCount < 2) {
        // 🔧 FIX: Retry on server errors
        console.log(`⚠️ Profile check failed (${response.status}), retrying... (${retryCount + 1}/2)`);
        await new Promise(r => setTimeout(r, 2000));
        return checkProfileComplete(userId, retryCount + 1);
      } else {
        console.error('❌ API error after retries:', response.status);
        // Try to use cached profile
        const cached = localStorage.getItem(`profile_${userId}`);
        if (cached) {
          console.log('📦 Using cached profile');
          const profileData = JSON.parse(cached);
          setProfile(profileData);
          setProfileComplete(profileData.name && (profileData.age || profileData.date_of_birth));
        } else {
          setProfileComplete(false);
          setProfile(null);
        }
      }
    } catch (error) {
      console.error('❌ Network error checking profile:', error);
      // 🔧 FIX: Retry on network errors
      if (retryCount < 2) {
        console.log(`⚠️ Network error, retrying profile check... (${retryCount + 1}/2)`);
        await new Promise(r => setTimeout(r, 2000));
        return checkProfileComplete(userId, retryCount + 1);
      }
      // Try to use cached profile
      const cached = localStorage.getItem(`profile_${userId}`);
      if (cached) {
        console.log('📦 Using cached profile after network error');
        const profileData = JSON.parse(cached);
        setProfile(profileData);
        setProfileComplete(profileData.name && (profileData.age || profileData.date_of_birth));
      } else {
        setProfileComplete(false);
        setProfile(null);
      }
    }
  };

  const loadNextRace = async (userId) => {
    // Cache-first surface (2026-06-05) — see loadMessageUsage above
    // for the rationale. Hydrate from localStorage so the home tab's
    // race countdown isn't blank during a 3-5 minute batch.
    try {
      const _cached = localStorage.getItem(`next_race_${userId}`);
      if (_cached) setNextRace(JSON.parse(_cached));
    } catch (_) { /* fall through to network */ }
    try {
      const _cachedGoal = localStorage.getItem(`next_goal_race_${userId}`);
      if (_cachedGoal) setNextGoalRace(JSON.parse(_cachedGoal));
    } catch (_) { /* fall through */ }
    try {
      const res = await authFetch(`${API_BASE_URL}/races?user_id=${userId}`);
      if (!res.ok) return;
      const data = await res.json();
      const races = data.races || data || [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const upcoming = races
        .filter(r => r.race_date && new Date(r.race_date + 'T00:00:00') >= today)
        .sort((a, b) => new Date(a.race_date) - new Date(b.race_date));

      // גם בדוק goal_date בפרופיל — מרוץ שנשמר רק בפרופיל (לא בטבלה)
      try {
        const profRes = await authFetch(`${API_BASE_URL}/profile?user_id=${userId}`);
        if (profRes.ok) {
          const prof = await profRes.json();
          if (prof.goal_date) {
            const profDate = new Date(prof.goal_date + 'T00:00:00');
            if (profDate >= today) {
              const alreadyInTable = upcoming.some(r => r.race_date?.slice(0,10) === prof.goal_date.slice(0,10));
              if (!alreadyInTable) {
                upcoming.push({ race_name: prof.goal_race || 'מרוץ יעד', race_date: prof.goal_date, distance: prof.goal_distance || '', is_goal_race: true });
                upcoming.sort((a, b) => new Date(a.race_date) - new Date(b.race_date));
              }
            }
          }
        }
      } catch {}

      const _next = upcoming[0] || null;
      setNextRace(_next);
      try { localStorage.setItem(`next_race_${userId}`, JSON.stringify(_next)); } catch (_) {}
      const _nextGoal = upcoming.find(r => r.is_goal_race) || null;
      setNextGoalRace(_nextGoal);
      try { localStorage.setItem(`next_goal_race_${userId}`, JSON.stringify(_nextGoal)); } catch (_) {}
    } catch (e) {
      // Cached value already on screen — silent fallback.
      console.log('📦 next-race: using cached value (backend busy)');
    }
  };

  // Returns { days, name } for the NEAREST upcoming race across all sources
  const getNearestRace = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const ms = 1000 * 60 * 60 * 24;
    try {
      const candidates = [];
      // Each candidate must carry `distance` — the phase widget reads it
      // off `_nearestRace.distance` to pick the right TAPER_WINDOWS row.
      // Without it the widget falls back to profile.goal_distance, which
      // is often a long-term target (e.g. "מרתון") that doesn't match
      // the nearest upcoming race (e.g. a 10K) and produces a wrong phase.
      if (nextRace?.race_date) {
        const d = Math.round((new Date(nextRace.race_date + 'T00:00:00') - today) / ms);
        if (d >= 0) candidates.push({
          days: d,
          name: nextRace.race_name || 'המרוץ',
          distance: nextRace.distance || '',
        });
      }
      if (profile?.goal_date) {
        const d = Math.round((new Date(profile.goal_date + 'T00:00:00') - today) / ms);
        if (d >= 0) candidates.push({
          days: d,
          name: profile.goal_race || 'המרוץ',
          distance: profile.goal_distance || '',
        });
      }
      if (!candidates.length) return null;
      return candidates.sort((a, b) => a.days - b.days)[0];
    } catch { return null; }
  };

  // Returns nearest race with is_goal_race=True — used for phase widget only.
  // Keeps phase widget in sync with backend (which ignores training races for taper).
  const getNearestGoalRace = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const ms = 1000 * 60 * 60 * 24;
    try {
      const candidates = [];
      if (nextGoalRace?.race_date) {
        const d = Math.round((new Date(nextGoalRace.race_date + 'T00:00:00') - today) / ms);
        if (d >= 0) candidates.push({ days: d, name: nextGoalRace.race_name || 'המרוץ', distance: nextGoalRace.distance || '' });
      }
      if (profile?.goal_date) {
        const d = Math.round((new Date(profile.goal_date + 'T00:00:00') - today) / ms);
        if (d >= 0) candidates.push({ days: d, name: profile.goal_race || 'המרוץ', distance: profile.goal_distance || '' });
      }
      if (!candidates.length) return null;
      return candidates.sort((a, b) => a.days - b.days)[0];
    } catch { return null; }
  };

  const _nearestRace = getNearestRace();
  const _nearestGoalRace = getNearestGoalRace();
  // Use ?? not || so days=0 (race day) doesn't collapse to null.
  const calculateDaysToRace = () => _nearestRace?.days ?? null;

  const daysToRace = calculateDaysToRace();

  // Handle successful auth
  const handleAuthSuccess = async (firebaseUser) => {
    setUser(firebaseUser);
    await Promise.all([
      checkUserConsent(firebaseUser.uid),
      checkProfileComplete(firebaseUser.uid),
    ]);
  };

  // Handle logout
  const handleLogout = async () => {
    // Unregister THIS device's push token while still signed in, so a shared browser does not keep
    // receiving the previous user's workout notifications. Best effort — never blocks logout.
    try {
      const fcmToken = localStorage.getItem('fcm-token');
      if (fcmToken && user?.uid) {
        await authFetch(
          `${API_BASE_URL}/unregister-push-token/${encodeURIComponent(user.uid)}?fcm_token=${encodeURIComponent(fcmToken)}`,
          { method: 'DELETE', signal: AbortSignal.timeout(5000) }
        );
      }
    } catch (e) {
      console.warn('Push token unregister on logout failed:', e);
    }
    ['push-registered', 'fcm-token', 'fcm-token-synced-at'].forEach((k) => localStorage.removeItem(k));
    await logOut();
    setUser(null);
    setProfileComplete(null);
    setProfile(null);
    setCurrentPage('home');
  };

  // Handle consent
  // Called by ConsentScreen ONLY after POST /consent succeeded (the backend holds the record).
  const handleConsent = () => {
    setHasConsented(true);
  };

  // Handle onboarding complete
  const [fromOnboarding, setFromOnboarding] = useState(false);

  const handleOnboardingComplete = () => {
    // Loud marker so we can verify in the console which code path ran.
    // If you DON'T see '[STATE_1]' here on the new build, the bundle is
    // stale.
    console.log('✅ [STATE_1] Onboarding completed — routing to onboarding-loading');
    localStorage.setItem(`onboarding_${user.uid}`, 'completed');
    // State 1 architecture: the wizard now hands off to the fullscreen
    // OnboardingLoader, NOT to chat. The loader owns plan generation
    // (current week + next week) and fades into the dashboard when
    // done. The chat-based welcome flow (showOnboardingChat_*) is
    // retired so we don't double-fire plan generation.
    setHasCompletedOnboarding(true);
    setProfileComplete(true);
    setFromOnboarding(true);
    setCurrentPage('onboarding-loading');
    setTimeout(() => checkProfileComplete(user.uid), 1500);
  };

  // Check if user is admin
  const adminEmails = ['liorpar@gmail.com'];
  const isAdmin = user?.email && adminEmails.includes(user.email.toLowerCase());

  // ==================== MAIN APP CONTENT ====================
  const MainAppContent = () => {
    // בדיקת תקשורת ראשונית — מסך המתנה עד שהפינג חוזר
    if (checkingConnection) {
      return (
        <div className="min-h-screen bg-bg flex items-center justify-center p-4" dir="rtl">
          <div className="text-center">
            <div className="text-5xl mb-4 animate-pulse">📡</div>
            <p className="text-text-muted text-sm">{connectingMsg}</p>
          </div>
        </div>
      );
    }

    // Offline screen — shown before everything else
    if (isOffline) {
      return (
        <div className="min-h-screen bg-bg flex items-center justify-center p-4" dir="rtl">
          <div className="bg-surface rounded-2xl p-8 max-w-sm w-full text-center border border-border">
            <div className="text-6xl mb-4">📵</div>
            <h2 className="text-xl font-bold text-text mb-3">אין חיבור לאינטרנט</h2>
            <p className="text-text-muted text-sm mb-6">
              האפליקציה דורשת חיבור לאינטרנט כדי לפעול.<br />
              בדוק את חיבור ה-WiFi או הנתונים הסלולריים.
            </p>
            <button
              onClick={() => {
                setCheckingConnection(true);
                fetch(`${API_BASE_URL}/health`, { signal: AbortSignal.timeout(5000) })
                  .then(() => { setIsOffline(false); setCheckingConnection(false); })
                  .catch(() => { setIsOffline(true); setCheckingConnection(false); });
              }}
              className="w-full bg-accent text-on-accent font-bold py-3 px-6 rounded-full transition-colors"
            >
              🔄 נסה שוב
            </button>
          </div>
        </div>
      );
    }

    // Loading state
    if (loading || checkingConsent) {
      return (
        <div className="min-h-screen bg-bg flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-pressed flex items-center justify-center mb-4 animate-pulse">
              <img src="/icons/ritmoiq-r.png" alt="RitmoIQ" className="w-full h-full rounded-2xl object-cover" />
            </div>
            <p className="text-text-muted">טוען...</p>
          </div>
        </div>
      );
    }

    // Not logged in - show auth page
    if (!user) {
      return <AuthPage onAuthSuccess={handleAuthSuccess} inviteCode={inviteCode} />;
    }

    // Still loading profile after login
    if (profileComplete === null) {
      return (
        <div className="min-h-screen bg-bg flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-pressed flex items-center justify-center mb-4 animate-pulse">
              <img src="/icons/ritmoiq-r.png" alt="RitmoIQ" className="w-full h-full rounded-2xl object-cover" />
            </div>
            <p className="text-text-muted">טוען פרופיל...</p>
          </div>
        </div>
      );
    }

    // Still checking user type
    if (checkingUserType) {
      return (
        <div className="min-h-screen bg-bg flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-pressed flex items-center justify-center mb-4 animate-pulse">
              <img src="/icons/ritmoiq-r.png" alt="RitmoIQ" className="w-full h-full rounded-2xl object-cover" />
            </div>
            <p className="text-text-muted">בודק חשבון...</p>
          </div>
        </div>
      );
    }

    // 🔧 FIX: Show connection error screen instead of consent for existing users
    if (connectionError) {
      return (
        <div className="min-h-screen bg-bg flex items-center justify-center p-4" dir="rtl">
          <div className="bg-surface border border-border rounded-2xl p-8 max-w-md w-full text-center">
            <div className="text-6xl mb-4">📡</div>
            <h2 className="text-2xl font-bold text-text mb-4">בעיית חיבור לשרת</h2>
            <p className="text-text-muted mb-6">
              לא הצלחנו לטעון את הפרופיל שלך.
              <br/>ייתכן שהשרת נמצא בהפעלה מחדש.
            </p>
            <button
              onClick={() => {
                setConnectionError(false);
                checkUserConsent(user.uid);
                checkProfileComplete(user.uid);
              }}
              className="w-full bg-accent text-on-accent font-bold py-3 px-6 rounded-full transition-colors mb-3"
            >
              🔄 נסה שוב
            </button>
            <p className="text-sm text-text-muted">
              אם הבעיה נמשכת, נסה לרענן את הדף או להתחבר מחדש
            </p>
          </div>
        </div>
      );
    }

    // Consent version mismatch: this build's legal texts are not the version the server requires.
    // Fail closed and ask for a refresh instead of offering stale texts for acceptance.
    if (consentVersionMismatch) {
      return (
        <div className="min-h-screen bg-bg flex items-center justify-center p-4" dir="rtl">
          <div className="bg-surface border border-border rounded-2xl p-8 max-w-md w-full text-center">
            <div className="text-6xl mb-4">🔄</div>
            <h2 className="text-2xl font-bold text-text mb-4">יש גרסה חדשה של האפליקציה</h2>
            <p className="text-text-muted mb-6">
              מסמכי ההסכמה עודכנו. יש לרענן את הדף כדי להמשיך.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-accent text-on-accent font-bold py-3 px-6 rounded-full transition-colors"
            >
              רענון הדף
            </button>
          </div>
        </div>
      );
    }

    // Consent check
    if (!hasConsented && !checkingConsent) {
      return (
        <ConsentScreen
          onConsent={handleConsent}
          userId={user.uid}
          userEmail={user.email}
        />
      );
    }

    // Onboarding check - show OnboardingWizard if profile incomplete or onboarding not completed
    if (hasConsented && !hasCompletedOnboarding && !checkingOnboarding && !checkingUserType && userType !== 'b2b') {
      const hasInvite = inviteCode || pendingInviteCode || localStorage.getItem('pending_invite_code');
      
      return (
        <OnboardingWizard
          userId={user.uid}
          userEmail={user.email}
          onComplete={handleOnboardingComplete}
          isJoiningCoach={!!hasInvite}
          onViewPlan={() => {
            // Plan was already built in Step6 → mark complete + go straight to the
            // plan page (skip the loader/plan-gen re-run that handleOnboardingComplete
            // would trigger via 'onboarding-loading').
            localStorage.setItem(`onboarding_${user.uid}`, 'completed');
            try { sessionStorage.removeItem(`onb_step_${user.uid}`); } catch {}
            setHasCompletedOnboarding(true);
            setProfileComplete(true);
            setCurrentPage('weekly-plan');
          }}
        />
      );
    }

    // Join coach from invite link
    if (inviteCode && hasCompletedOnboarding) {
      return (
        <JoinCoach
          inviteCode={inviteCode}
          userId={user.uid}
          userEmail={user.email}
          userName={profile?.name || user.email?.split('@')[0]}
          onSuccess={(data) => {
            alert(`🎉 הצטרפת בהצלחה לקבוצה של ${data.coach_name}!`);
            setInviteCode(null);
            setPendingInviteCode(null);
            localStorage.removeItem('pending_invite_code');
            window.history.replaceState({}, '', '/');
            checkUserType(user.uid);
          }}
          onCancel={() => {
            setPendingInviteCode(inviteCode);
            setInviteCode(null);
            window.history.replaceState({}, '', '/');
          }}
        />
      );
    }

    // Chat access is gated by the backend (user_access.daily_limit) — admins
    // grant athletes chat via /admin/user/approve or the backfill endpoint.
    // We do not block by userType here; the button is already disabled when
    // messagesRemaining === 0, and the /chat endpoint returns 'blocked' when
    // the user lacks approval.

    // Premium v2 bottom bar (PREMIUM_TODAY_ENABLED): ONE shared bar on the four main screens — Today, Plan, Coach,
    // Activities. Same routes and permissions as before: the paid chat stays locked for B2B athletes (upgrade modal on
    // Home), and leaving the chat refreshes the message counter exactly like its back button.
    const navigateMain = (page) => {
      if (page === currentPage) { try { window.scrollTo(0, 0); } catch { /* jsdom */ } return; }
      if (page === 'chat' && userType === 'b2b') { setCurrentPage('home'); setShowChatUpgradeModal(true); return; }
      if (currentPage === 'chat') loadMessageUsage(user.uid).catch(() => {});
      setFromOnboarding(false);
      setCurrentPage(page);
    };
    // The shell also sets where each tab opens: Today / Plan / Activities at the top, Coach at its latest message
    // (components/today/PremiumNavShell.jsx).
    const withPremiumNav = (active, element) => (HOME_GRAPHITE_ENABLED ? (
      <PremiumNavShell active={active} onNavigate={navigateMain} Nav={GraphiteBottomNav}>{element}</PremiumNavShell>
    ) : PREMIUM_TODAY_ENABLED ? (
      <PremiumNavShell active={active} onNavigate={navigateMain}>{element}</PremiumNavShell>
    ) : element);

    // Page routing
    if (currentPage === 'activities') {
      return withPremiumNav('activities', <ActivityList onBack={() => setCurrentPage('home')} userId={user.uid} />);
    }

    if (currentPage === 'chat') {
      // Build dynamic chips context
      const today = new Date().toISOString().split('T')[0];
      const allActs = Array.isArray(lastActivity) ? lastActivity : [];
      const todayAct = allActs.find(a => (a.date || a.startTimeLocal || '').slice(0,10) === today);
      const injury = profile?.injury_prone_areas || '';

      return withPremiumNav('chat', <Chat
        onBack={() => {
          // Refresh counter from server when leaving chat — bug 2026-06-10:
          // chat updates its OWN local count but App.js's messagesRemaining
          // (shown in home counter) stayed stale until full app reload.
          loadMessageUsage(user.uid).catch(() => {});
          setCurrentPage('home');
          setFromOnboarding(false);
        }}
        userId={user.uid}
        profileComplete={profileComplete}
        fromOnboarding={fromOnboarding}
        onNavigateToWeeklyPlan={() => { setFromOnboarding(false); setCurrentPage('weekly-plan'); }}
        onNavigateToRaces={(prefill) => {
          // Chat → Races deep-link with prefilled form.
          // Stash whatever we have (name + optional date); Races.jsx reads + clears.
          if (prefill && prefill.race_name) {
            try { localStorage.setItem('races_prefill', JSON.stringify(prefill)); } catch {}
          }
          setFromOnboarding(false);
          setRacesReturnTo('chat');
          setCurrentPage('races');
        }}
        onNavigateToProfile={(section) => {
          // Chat -> Profile at that field's card (navigation only; the profile's save offers the rebuild).
          setFromOnboarding(false);
          setProfileFocus(section || 'training');
          setProfileReturnTo('chat');
          setCurrentPage('profile');
        }}
        todayActivity={todayAct || null}
        injuryArea={injury}
        nextRace={nextRace || null}
        weeklyPlanPhase={weeklyPlanPhase || null}
        sportsActive={(profile?.sports_active || 'running').split(',').map(s => s.trim()).filter(Boolean)}
      />);
    }

    if (currentPage === 'profile') {
      const profileNav = (el) => (HOME_GRAPHITE_ENABLED ? withPremiumNav('profile', el) : el);
      return profileNav(
        <Profile
          focusSection={profileFocus}
          onBack={() => {
            checkProfileComplete(user.uid);
            const to = profileReturnTo || 'home'; setProfileReturnTo(null); setProfileFocus(null);
            setCurrentPage(to);
          }}
          userId={user.uid}
          userEmail={user.email}
          onLogout={handleLogout}
          onProfileSaved={() => {
            setProfileComplete(true);
            checkProfileComplete(user.uid);
          }}
        />
      );
    }

    if (currentPage === 'onboarding-loading') {
      // Sport selection comes from the cycling profile's sports_active
      // field (set by Step 6 of the wizard). Default to running-only
      // when the field is missing or hasn't reached the profile state
      // yet — better to generate one plan than zero.
      const sportsCsv = profile?.sports_active || 'running';
      const sports = sportsCsv.split(',').map(s => s.trim()).filter(Boolean);
      return (
        <OnboardingLoader
          userId={user.uid}
          sports={sports}
          profile={profile}
          onComplete={() => {
            setFromOnboarding(false);
            setCurrentPage('weekly-plan');
          }}
          onBack={() => {
            setFromOnboarding(false);
            setCurrentPage('home');
          }}
        />
      );
    }

    if (currentPage === 'weekly-plan') {
      // isAdmin gates the manual 'צור תכנית' button — while State 1+2
      // automatic generation is still being stabilised we only want the
      // admin (liorpar@gmail.com) to be able to trigger plan generation
      // by hand for testing. Regular users see an explanatory message.
      return withPremiumNav('weekly-plan', <WeeklyPlan onBack={() => setCurrentPage('home')} userId={user.uid} isAdmin={isAdmin} onPlanGenerated={() => loadMessageUsage(user.uid)} />);
    }

    if (currentPage === 'races') {
      return <Races onBack={() => { const to = racesReturnTo || 'home'; setRacesReturnTo(null); setCurrentPage(to); }}
                    userId={user.uid} />;
    }

    if (currentPage === 'coach') {
      return (
        <CoachDashboard 
          userId={user.uid}
          userEmail={user.email}
          onBack={() => setCurrentPage('home')}
        />
      );
    }

    if (currentPage === 'admin') {
      return (
        <AdminPanel
          userId={user.uid}
          userEmail={user.email}
          onBack={() => setCurrentPage('home')}
        />
      );
    }

    if (currentPage === 'insights') {
      return (
        <Home
          userId={user.uid}
          userName={profile?.name}
          onBack={() => setCurrentPage('home')}
          isAdmin={isAdmin}
        />
      );
    }

    // B2B chat upgrade modal (the locked coach tab) — the same modal on both Homes.
    const chatUpgradeModal = showChatUpgradeModal && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
                onClick={() => setShowChatUpgradeModal(false)}
                dir="rtl"
              >
                <div
                  className="bg-surface border border-border rounded-card shadow-card max-w-md w-full p-6"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="text-center mb-5">
                    <div className="w-11 h-11 rounded-control bg-accent-soft text-accent flex items-center justify-center mx-auto mb-2.5">
                      <ChatCircle size={22} weight="bold" />
                    </div>
                    <h2 className="text-text text-xl font-bold m-0">צ'אט עם מאמן ה-AI</h2>
                    <p className="text-accent text-sm mt-1">זמין במנוי שדרוג</p>
                  </div>

                  <p className="text-text-body text-sm leading-relaxed mb-4">
                    כמתאמן של מאמן, המאמן שלך הוא הקואצ' האנושי שלך —
                    הוא עורך את התכנית ועונה לך ישירות. עם המנוי תקבל
                    גם <span className="font-semibold text-accent">צ'אט AI 24/7</span> במקביל.
                  </p>

                  <div className="bg-surface-2 border border-border rounded-control p-4 mb-5 flex flex-col gap-3">
                    <p className="text-text-muted text-xs m-0">דוגמאות לשימוש בצ'אט AI:</p>
                    {[
                      '"נתח את האימון של אתמול — מה הקצב שלי אומר?"',
                      '"הייתי חולה השבוע, האם להשתתף במרוץ?"',
                      '"איך לנהל קצב במרוץ 10K עם רוח חזיתית?"',
                    ].map((q) => (
                      <p key={q} className="text-text text-sm m-0">{q}</p>
                    ))}
                  </div>

                  <div className="bg-accent-soft border border-accent/35 rounded-control p-4 mb-5 text-center">
                    <p className="text-text text-2xl font-bold m-0">₪19.90<span className="text-base font-normal text-text-muted"> / חודש</span></p>
                    <p className="text-text-muted text-xs mt-1">ניתן לבטל בכל עת</p>
                  </div>

                  <Button
                    className="mb-2"
                    onClick={() => {
                      // Placeholder: real upgrade flow (Stripe / Paddle / etc.) will replace this.
                      // For now show a 'coming soon' toast.
                      alert('שדרוג בתשלום יזמין בקרוב. בינתיים תפנה למאמן שלך.');
                      setShowChatUpgradeModal(false);
                    }}
                  >שדרג עכשיו</Button>

                  <Button variant="secondary" onClick={() => setShowChatUpgradeModal(false)}>
                    אולי בפעם אחרת
                  </Button>
                </div>
              </div>
    );

    // Home Page — Graphite + Lime (HOME_GRAPHITE_ENABLED, 2026-10-27): replaces the whole previous Home (banners,
    // cards, quick-access list). The role-gated coach / admin entries stay (the only way into those screens).
    if (HOME_GRAPHITE_ENABLED) {
      return withPremiumNav('home', (
        <>
          <HomeGraphiteHome
            userId={user.uid}
            trend={workoutTrend}
            nextRace={nextRace?.race_name ? { name: nextRace.race_name, days: daysToRace } : null}
            onNavigate={navigateMain}
            onConnect={() => setCurrentPage('profile')}
            onDeepAnalyze={startDeepAnalysis}
            deepAnalyzing={deepAnalyzing}
            onViewDeepAnalysis={() => openSavedDeepAnalysis(workoutTrend)}
            injury={profile ? { state: profile.injury_state, returnStart: profile.return_start_date } : null}
            onUpdateInjury={() => {
              // "עדכון מצב פציעה" (owner 2026-11-02): navigation only — the profile opens at its injury card
              // (Profile focusSection -> [data-testid="pf-health"]); nothing is written, no rebuild, no dialog. The
              // return starts when the athlete saves "כשיר" there.
              setFromOnboarding(false);
              setProfileFocus('health');
              setProfileReturnTo('home');
              setCurrentPage('profile');
            }}
            footer={(userType === 'coach' || isAdmin) ? (
              <div dir="rtl">
                <PrivilegedSection showCoach={userType === 'coach' || isAdmin} showAdmin={isAdmin}
                                   onCoach={() => setCurrentPage('coach')} onAdmin={() => setCurrentPage('admin')} />
              </div>
            ) : null}
          />
          <DeepAnalysisViewer
            open={!!deepView}
            loading={!!deepView?.loading}
            text={deepView?.text}
            date={deepView?.date}
            onClose={() => setDeepView(null)}
          />
          {chatUpgradeModal}
        </>
      ));
    }

    return withPremiumNav('home', (
      <div className={`min-h-screen ${PREMIUM_TODAY_ENABLED ? 'premium-page-bg' : 'bg-bg'}`}>
      <div className="max-w-lg mx-auto min-h-screen flex flex-col" dir="rtl">

        {/* Header — sign-out lives in the profile screen; no server-waking banner
            (infra detail, dropped per handoff_app_design §1.9). */}
        {!PREMIUM_TODAY_ENABLED && (
          <Header logo actions={[{ icon: <User size={19} weight="bold" />, onClick: () => setCurrentPage('profile'), label: 'פרופיל' }]} />
        )}


        {/* Main Content */}
        <main className="relative z-10 container mx-auto px-4 py-6 flex-1">
          <div className="max-w-lg mx-auto">

            {/* ── Home screen content — order per handoff_app_design README §5.1: nothing above
                the countdown, then countdown → forecast → today → insight → chat, in that exact
                order. Banners/notifications/b2b messages are secondary attention items with no
                specified position in §5.1's numbered list — placed after the core stack and
                before the quick-access list group (a judgment call, not an explicit README
                position) so they never sit between the header and the countdown. ── */}
            {(() => {
              // hasRace: daysToRace can legitimately be 0 (race day) — null check, not truthy.
              const hasRaceDate = daysToRace !== null;
              // Loading skeleton — hero + workout card placeholders, no fake countdown (§States).
              if (!homeDataReady) return (
                <div className="mb-3 flex flex-col gap-3">
                  <Skeleton rounded="card" className="h-36" />
                  <Skeleton rounded="card" className="h-44" />
                  <Skeleton rounded="card" className="h-28" />
                </div>
              );

              // Training phase (always computed)
              const trainingPhase = getTrainingPhase(
                _nearestGoalRace?.days ?? daysToRace,
                _nearestGoalRace?.distance || nextRace?.distance || profile?.goal_distance,
                _nearestGoalRace?.name || nextRace?.race_name || profile?.goal_race,
              );

              const raceName = _nearestGoalRace?.name || _nearestRace?.name || nextRace?.race_name || 'המרוץ שלך';
              const raceDistRaw = _nearestGoalRace?.distance || _nearestRace?.distance || nextRace?.distance || profile?.goal_distance;
              const raceDist = ({ '5': '5 ק״מ', '10': '10 ק״מ', '21.1': 'חצי מרתון', '42.2': 'מרתון' })[String(raceDistRaw || '')] || raceDistRaw;

              const nxt = workoutTrend?.next_workout ?? (() => {
                const kw = homeInsights?.indicators?.next_quality_workout;
                return kw ? {
                  date: null, workout_type: null, label: kw.type,
                  planned_distance: kw.km, planned_pace: kw.pace_str,
                  day_name: kw.day_name, description: null, prep_note: null,
                } : null;
              })();

              const chatLocked = userType === 'b2b';
              const openChat = () => {
                // B2B athlete: chat is paywalled. Show upgrade modal instead of opening the chat
                // (server-side gate would 403 anyway).
                if (chatLocked) { setShowChatUpgradeModal(true); return; }
                // Quota exhausted still navigates — history stays readable.
                setCurrentPage('chat');
              };

              // Coach-insight handlers — shared by the current Home card and the Premium Today screen
              const insightProps = {
                pendingContext: pendingContextWorkout,
                contextSaving: contextSaving,
                onContextSave: async (flag) => {
                  if (!user) return;
                  const date = pendingContextWorkout?.date || homeInsights?.last_workout?.date;
                  if (!date) return;
                  setContextSaving(true);
                  try {
                    const params = new URLSearchParams({ user_id: user.uid, date, flag });
                    await authFetch(`${API_BASE_URL}/plan-history/context?${params}`, { method: 'POST' });
                    setPendingContextWorkout(null);
                  } catch (e) {
                    showToast('שגיאה בשמירה', 'error');
                  } finally {
                    setContextSaving(false);
                  }
                },
                onContextSkip: () => {
                  if (!user) return;
                  const date = pendingContextWorkout?.date || homeInsights?.last_workout?.date;
                  if (!date) return;
                  const params = new URLSearchParams({ user_id: user.uid, date, flag: 'normal' });
                  authFetch(`${API_BASE_URL}/plan-history/context?${params}`, { method: 'POST' }).catch(() => {});
                  setPendingContextWorkout(null);
                },
                deepAnalyzing: deepAnalyzing,
                onViewDeepAnalysis: () => openSavedDeepAnalysis(workoutTrend),
                onDeepAnalyze: startDeepAnalysis,
              };

              // Premium Today (config/features.PREMIUM_TODAY_ENABLED): the redesigned stack; the banners, Garmin
              // card and the rest of Home below are unchanged. The previous stack renders when the flag is off.
              if (PREMIUM_TODAY_ENABLED) return (
                <div style={{ marginBottom: 14 }}>
                  <PremiumToday
                    userId={user.uid}
                    profile={profile}
                    trend={workoutTrend}
                    onOpenProfile={() => setCurrentPage('profile')}
                    onOpenConnections={() => setCurrentPage('profile')}
                    onNavigate={(page) => (page === 'chat' ? openChat() : setCurrentPage(page))}
                    {...insightProps}
                  />
                </div>
              );

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 14 }}>

                  {/* 1. Race countdown hero — only with a real race date; no fake countdown */}
                  {hasRaceDate && daysToRace > 0 && (
                    <RaceHero
                      raceName={raceName}
                      raceDistance={raceDist}
                      days={daysToRace}
                      phaseInfo={trainingPhase}
                      onClick={() => setCurrentPage('races')}
                    />
                  )}

                  {/* 2. (2026-10-27 product decision) the training forecast is not shown anywhere in the app — the
                      forecast card and its /pace-prediction read are removed, also from this rollback Home. */}

                  {/* 3. Today's / next workout — the primary action */}
                  <TodayWorkout next={nxt} onViewPlan={() => setCurrentPage('weekly-plan')} />

                  {/* 4. Coach insight — the anchor card, with the feedback loop */}
                  {workoutTrend && (
                    <InsightA2
                      trend={workoutTrend}
                      {...insightProps}
                    />
                  )}

                  {/* 5. Chat — the most important entry point, a framed card (never a list row) */}
                  <ChatCardA2
                    data={chatCard}
                    loading={chatCardLoading && !chatCard}
                    remaining={messagesRemaining}
                    locked={chatLocked}
                    onOpen={openChat}
                    onAsk={(q) => {
                      if (chatLocked) { setShowChatUpgradeModal(true); return; }
                      if (!user) return;
                      // Consume locally so the chip doesn't linger, stash the question for
                      // Chat's auto-send, and land on the coach answering (not a pre-filled input).
                      setChatCard(prev => prev ? { ...prev, questions: (prev.questions || []).filter(x => x.key !== q.key) } : prev);
                      try { localStorage.setItem(`pendingQuestion_${user.uid}`, q.text); } catch {}
                      setCurrentPage('chat');
                    }}
                  />

                </div>
              );
            })()}

            {/* Notification banner */}
            <div className="mt-3.5">
              <NotificationButton userId={user.uid} />
            </div>

            {/* Injury v2 banner — 3-state model (2026-06-25) */}
            {profile && (profile.injury_state === 'pain' || profile.injury_state === 'cant_run') && (() => {
              const state = profile.injury_state;
              const startDate = profile.injury_start_date;
              const returnDate = profile.return_start_date;

              // Compute days since injury started (frontend, no round-trip)
              let daysInjured = null;
              if (startDate) {
                const start = new Date(startDate);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                daysInjured = Math.max(0, Math.round((today - start) / 86400000));
              }

              // Compute return progress — CALENDAR weeks (Sunday start), identical to the backend's
              // _injury_v2_return_week: the week containing the return date is week 1 (rw0), the
              // next calendar week is week 2, etc. Raw day arithmetic made a mid-week return show a
              // label one week behind the plan's ramp.
              let returnWeek = 0;
              if (returnDate) {
                const sundayOf = (d) => {
                  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
                  x.setDate(x.getDate() - x.getDay());
                  return x;
                };
                const [ry, rm, rd] = String(returnDate).slice(0, 10).split('-').map(Number);
                const ret = new Date(ry, rm - 1, rd);   // local date — no UTC shift
                returnWeek = Math.max(0, Math.round((sundayOf(new Date()) - sundayOf(ret)) / 604800000));
              }
              const isReturning = !!returnDate;

              const isCantRun = state === 'cant_run';

              const title = isCantRun
                ? (isReturning ? `חזרה הדרגתית — שבוע ${returnWeek + 1}` : 'פציעה פעילה — מנוחה מלאה')
                : (isReturning ? `חזרה הדרגתית — שבוע ${returnWeek + 1}` : 'כאב פעיל — נפח מופחת');

              const body = isCantRun && !isReturning
                ? 'התכניות שלך במנוחה מלאה. כשהכאב יורגע ויש אישור מקצועי לחזור — לחץ "מוכן לחזור".'
                : isReturning
                  ? `חזרה הדרגתית בעיצומה. נפח ועצימות עולים בהדרגה — ${returnWeek < 4 ? 'תמשיך לאט ובזהירות' : 'עוד מעט בחזרה לנורמל'}.`
                  : 'התכניות הותאמו — נפח מופחת, ללא עצימויות גבוהות. כשהכאב יחלוף — עדכן "כרגיל" בפרופיל.';

              const handleReturn = startInjuryReturn;   // the shared Return to Run start (component level)

              return (
                <div className="mb-4">
                  <A2Banner
                    Icon={FirstAid}
                    accent="quality"
                    title={title}
                    chip={daysInjured !== null ? `יום ${daysInjured} לפציעה` : null}
                    body={body}
                    actions={[
                      { label: 'עדכן סטטוס', onClick: () => setCurrentPage('profile') },
                      ...(isCantRun && !isReturning
                        ? [{ label: 'מוכן לחזור', onClick: handleReturn, kind: 'ghost' }] : []),
                    ]}
                  />
                </div>
              );
            })()}

            {/* Profile Incomplete — A2 banner (amber caution) */}
            {profileComplete === false && (
              <div className="mb-4">
                <A2Banner
                  Icon={Warning}
                  accent="quality"
                  title="הפרופיל שלך לא מלא"
                  body="מלא את הפרופיל כדי לקבל ניתוחים ותכנית מדויקים"
                  actions={[{ label: 'השלם פרופיל', onClick: () => setCurrentPage('profile') }]}
                />
              </div>
            )}

            {/* Garmin connection status (Strava removal C2, 2026-10-21): connected / not connected / checking / can't
                check. Leads to Profile → "חיבורי אימונים" (the existing Garmin connect flow lives there). */}
            <TrainingSourcesCard onManage={() => setCurrentPage('profile')} />

            {/* Pending coach invite — A2 banner (quiet slate, privileged tone) */}
            {pendingInviteCode && userType !== 'b2b' && (
              <div className="mb-4">
                <A2Banner
                  Icon={UserPlus}
                  accent="accent"
                  title="יש לך הזמנה להצטרף למאמן"
                  body="הצטרף לקבוצה וקבל ליווי מקצועי"
                  actions={[{ label: 'הצטרף עכשיו', onClick: () => setInviteCode(pendingInviteCode) }]}
                  onDismiss={() => {
                    setPendingInviteCode(null);
                    localStorage.removeItem('pending_invite_code');
                  }}
                />
              </div>
            )}

            {/* Coach Messages - for B2B athletes */}
            {userType === 'b2b' && (
              <div className="mb-4">
                <AthleteMessages userId={user.uid} />
              </div>
            )}

            {/* "עוד באפליקציה" list group (§5.1 item 7 / §4.3): plan, activities, races — chat has
                its own framed card above, not a list row. A5: sub-lines are live data (today's
                workout, the last analyzed activity, days to the next race), never a generic
                description — all sourced from data already fetched for this screen. */}
            <div className="mb-4">
              {(() => {
                const HEB_DAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

                // Plan row — today's prescribed workout, when the next one IS today (same source
                // TodayWorkout renders from). Falls back to the original generic line otherwise.
                const _nxt = workoutTrend?.next_workout ?? (() => {
                  const kw = homeInsights?.indicators?.next_quality_workout;
                  return kw ? { date: null, label: kw.type, planned_distance: kw.km } : null;
                })();
                let _nxtIsToday = false;
                if (_nxt?.date && /^\d{4}-\d{2}-\d{2}$/.test(String(_nxt.date).slice(0, 10))) {
                  const [y, m, d] = String(_nxt.date).slice(0, 10).split('-').map(Number);
                  const dt = new Date(y, m - 1, d);
                  const now = new Date();
                  _nxtIsToday = dt.getFullYear() === now.getFullYear() && dt.getMonth() === now.getMonth() && dt.getDate() === now.getDate();
                }
                const planSub = (_nxtIsToday && _nxt)
                  ? `היום: ${_nxt.label || 'אימון'}${_nxt.planned_distance ? `, ${_nxt.planned_distance} ק״מ` : ''}`
                  : 'השבוע שלך';

                // Activities row — the last analyzed workout (same source the insight card reads).
                const tw = workoutTrend?.today_workout;
                let actSub = '14 יום אחרונים';
                if (tw?.date) {
                  const [y, m, d] = String(tw.date).slice(0, 10).split('-').map(Number);
                  const dt = new Date(y, m - 1, d);
                  const now = new Date(); now.setHours(0, 0, 0, 0);
                  const diffDays = Math.round((now - dt) / 86400000);
                  const rel = diffDays === 0 ? 'היום' : diffDays === 1 ? 'אתמול' : (diffDays > 1 && diffDays < 7) ? HEB_DAYS[dt.getDay()] : null;
                  if (rel) {
                    actSub = `${rel}: ${tw.label || 'אימון'}${tw.distance ? ` ${tw.distance} ק״מ` : ''}${tw.pace ? `, ${tw.pace} לק״מ` : ''}`;
                  }
                }

                // Races row — days to the next race, matching the reference's "יבנה בעוד 23 יום".
                const raceSub = (nextRace?.race_name && daysToRace != null && daysToRace > 0)
                  ? `${nextRace.race_name} בעוד ${daysToRace === 1 ? 'יום אחד' : daysToRace === 2 ? 'יומיים' : `${daysToRace} יום`}`
                  : nextRace?.race_name
                    ? `הבא בתור: ${nextRace.race_name}${nextRace.is_goal_race ? ' (יעד)' : ''}`
                    : 'לוח מרוצים · ביצועים';

                return (
                  <QuickAccessList
                    premium={PREMIUM_TODAY_ENABLED}
                    planSub={planSub}
                    actSub={actSub}
                    raceSub={raceSub}
                    onPlan={() => setCurrentPage('weekly-plan')}
                    onActivities={() => setCurrentPage('activities')}
                    onRaces={() => setCurrentPage('races')}
                  />
                );
              })()}
            </div>

            {/* B2B chat upgrade modal — opens when a coach-attached athlete clicks the locked
                chat card. Shows 3 concrete use-cases + upgrade CTA. ARCHITECTURE.md §0: B2B
                athletes don't get chat by default; this is the upgrade gate. */}
            <DeepAnalysisViewer
              open={!!deepView}
              loading={!!deepView?.loading}
              text={deepView?.text}
              date={deepView?.date}
              onClose={() => setDeepView(null)}
            />


            {chatUpgradeModal}

            {/* Privileged section (§5.1): second list group under "הרשאות מורחבות", role-gated */}
            <div className="mt-3.5">
              <PrivilegedSection
                showCoach={userType === 'coach' || isAdmin}
                showAdmin={isAdmin}
                onCoach={() => setCurrentPage('coach')}
                onAdmin={() => setCurrentPage('admin')}
              />
            </div>

          </div>
        </main>

        {/* Footer */}
        <Footer />

        {/* PWA Install Prompt */}
        <InstallPrompt />
      </div>
      </div>
    ));
  };

  // ==================== ROUTER ====================
  return (
    <>
      {/* Global plan-regenerate banner — fixed at viewport top so it
          stays visible no matter which page MainAppContent rendered.
          Fired from Profile.jsx when the user accepts the 'rebuild
          plan' toast; the async fetch chain continues even after the
          profile screen unmounts because it's bound to the closure,
          and dispatches 'plan-regen-done' on completion. */}
      {planRegenStatus && (
        <div
          className={
            'fixed top-0 inset-x-0 z-[60] border-b px-4 py-2 text-center ' +
            (planRegenStatus.phase === 'running'
              ? 'bg-cyan-500/15 border-cyan-500/40 backdrop-blur'
              : planRegenStatus.phase === 'done'
              ? 'bg-green-500/15 border-green-500/40 backdrop-blur'
              : 'bg-red-500/15 border-red-500/40 backdrop-blur')
          }
          dir="rtl"
          role="status"
          aria-live="polite"
        >
          {planRegenStatus.phase === 'running' && (() => {
            // Derive accurate sport label from changedFields. Banner used
            // to hardcode "ריצה + רכיבה" even when only one sport was
            // being generated — confusing when the user unchecked cycling.
            const _fields = planRegenStatus.changedFields || [];
            const _has_run = _fields.some(f => /ריצה/.test(f));
            const _has_cyc = _fields.some(f => /רכיבה/.test(f));
            const _sport_label = _has_run && _has_cyc
              ? 'ריצה + רכיבה'
              : (_has_cyc ? 'רכיבה' : 'ריצה');
            const _suffix = planRegenMinutes > 0
              ? ` — ${planRegenMinutes} דק' עברו`
              : ' — התהליך עשוי לקחת 3-5 דקות';
            return (
              <p className="text-cyan-200 text-xs flex items-center justify-center gap-2">
                <span className="inline-block w-3 h-3 border-2 border-cyan-300 border-t-transparent rounded-full animate-spin" />
                {_fields.length > 0
                  ? <>בונה תכניות ({_sport_label}) לפי <span className="font-semibold">{_fields.join(', ')}</span>{_suffix}</>
                  : <>בונה תכניות חדשות לפי הפרופיל המעודכן{_suffix}</>}
              </p>
            );
          })()}
          {planRegenStatus.phase === 'done' && (
            <p className="text-green-200 text-xs">
              ✅ התוכנית מוכנה
              {planRegenStatus.changedFields && planRegenStatus.changedFields.length > 0
                ? <> ({planRegenStatus.changedFields.join(', ')})</>
                : null}
              {' '}— בדוק במסך התכנית השבועית.
            </p>
          )}
          {planRegenStatus.phase === 'error' && (
            <p className="text-red-200 text-xs">⚠️ יצירת תוכנית חדשה נכשלה. אפשר לנסות שוב מהפרופיל.</p>
          )}
        </div>
      )}
      <Router>
        <Routes>
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/privacy-en" element={<PrivacyPolicyEN />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/*" element={<MainAppContent />} />
        </Routes>
      </Router>
      <ToastHost />
    </>
  );
}

export default App;
